import './style.css';
import '@fontsource/noto-serif-tc/900.css';
import { PERSONALITIES } from './ai';
import { gameAudio } from './audio';
import type { VoiceCue } from './audio';
import { ELO_CALIBRATION, eloLabel, isCompleteCurrentElo, opponentElo } from './calibration';
import { OPPONENT_LINES } from './characterLines';
import { NARRATOR_INTROS, opponentIntroNarrationKey } from './narration';
import { createInitialGame, declareOutcome, generateLegalMoves, isInCheck, pieceLabel, playMove } from './engine';
import {
  createSearchWorker, engineLabel, isUsableResult, preferredBackend, shouldSoftCancelPikafish, supportsPikafishRuntime,
} from './engineAdapter';
import type { EngineBackend } from './engineAdapter';
import {
  canChangeOpponent, canUndo, DIFFICULTY_ORDER, highestRevealedDifficulty,
  isOpponentAvailable, isOpponentRevealed, isResumableGame, nextDifficulty, recordChallengeOutcome,
} from './gameplay';
import type { OutcomeReward } from './gameplay';
import { storyBestScore, storyBestScoreAfterUndos } from './leaderboardRules';
import { lookupOpeningMove } from './openingBook';
import {
  beginOnlineMatch, deleteCloudGame, fetchLeaderboard, hasOnlineMatchTicket, importPortableOnlineMatchTicket,
  loadCloudGame, loadOnlineIdentity, loginOnlinePlayer, portableOnlineMatchTicket, registerOnlinePlayer,
  renameOnlinePlayer, saveCloudGame, setOnlinePassword, submitOnlineResult,
} from './leaderboard';
import type { LeaderboardPlayer, LeaderboardSnapshot, OnlineIdentity } from './leaderboard';
import { capturedPieces, moveCueText } from './presentation';
import { matchPlayerAvatar, PLAYER_AVATARS } from './player';
import type { PlayerAvatar, PlayerGender, PlayerInterest } from './player';
import { analyzePostgame } from './postgameAnalysis';
import { OPPONENT_STORIES, resultStoryQuote, WORLD_STORY } from './story';
import {
  deleteNamedSave, deserializeGame, listNamedSaves, loadAutosave, loadProfile,
  putNamedSave, saveAutosave, saveProfile, serializeGame,
} from './storage';
import type { BoardStyle, Difficulty, GameMode, GameState, Move, MoveRecord, PlayerProfile, SearchResult, SearchWorkerMessage, Side } from './types';

const PIECE_IMAGE_URLS = import.meta.glob<string>('./assets/pieces-webp/*.webp', {
  eager: true, query: '?url', import: 'default',
});

function pieceImageUrl(style: BoardStyle, side: 'red' | 'black', type: string): string {
  const key = `./assets/pieces-webp/${style}-${side}-${type}.webp`;
  const url = PIECE_IMAGE_URLS[key];
  if (!url) throw new Error(`缺少棋子圖片：${key}`);
  return url;
}

const app = document.querySelector<HTMLDivElement>('#app');
if (!app) throw new Error('找不到應用程式根節點');

function syncViewportHeight(): void {
  document.documentElement.style.setProperty('--app-height', `${window.innerHeight}px`);
}
syncViewportHeight();
window.addEventListener('resize', syncViewportHeight);

const horizontalLines = Array.from({ length: 10 }, (_, row) => `<line x1="50" y1="${50 + row * 100}" x2="850" y2="${50 + row * 100}" />`).join('');
const verticalLines = Array.from({ length: 9 }, (_, col) => {
  const x = 50 + col * 100;
  return col === 0 || col === 8
    ? `<line x1="${x}" y1="50" x2="${x}" y2="950" />`
    : `<line x1="${x}" y1="50" x2="${x}" y2="450" /><line x1="${x}" y1="550" x2="${x}" y2="950" />`;
}).join('');

function positionMark(row: number, col: number): string {
  const x = 50 + col * 100;
  const y = 50 + row * 100;
  const sides = [col > 0 ? -1 : 0, col < 8 ? 1 : 0].filter(Boolean);
  return sides.map((side) => {
    const inner = x + side * 13;
    const outer = x + side * 28;
    return `<path d="M ${inner} ${y - 10} H ${outer} V ${y - 25} M ${inner} ${y + 10} H ${outer} V ${y + 25}" />`;
  }).join('');
}

const positionMarks = [
  [2, 1], [2, 7], [3, 0], [3, 2], [3, 4], [3, 6], [3, 8],
  [6, 0], [6, 2], [6, 4], [6, 6], [6, 8], [7, 1], [7, 7],
].map(([row, col]) => positionMark(row!, col!)).join('');

const calibrationDate = new Intl.DateTimeFormat('zh-TW', {
  year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'Asia/Taipei',
}).format(new Date(ELO_CALIBRATION.generatedAt));
const hasCompleteElo = isCompleteCurrentElo(Object.keys(PERSONALITIES) as Difficulty[]);
const eloRows = hasCompleteElo ? Object.values(PERSONALITIES).map((opponent) => {
  const result = opponentElo(opponent.id);
  return `<div class="elo-row" role="row"><span role="cell">${opponent.name}</span><strong role="cell">${result.rating}</strong><span role="cell">${result.ciLow}–${result.ciHigh}</span><span role="cell">${result.games} 局</span></div>`;
}).join('') : '';
const eloCalibrationDetails = hasCompleteElo
  ? `<p>${ELO_CALIBRATION.methodology} 本次共 ${ELO_CALIBRATION.totalGames} 局，決勝率 ${(ELO_CALIBRATION.decisiveRate * 100).toFixed(1)}%，更新於 ${calibrationDate}。</p><div class="elo-table" role="table" aria-label="十二位對手站內 Elo"><div class="elo-row elo-head" role="row"><span role="columnheader">對手</span><strong role="columnheader">Elo</strong><span role="columnheader">95% 區間</span><span role="columnheader">樣本</span></div>${eloRows}</div><small>${ELO_CALIBRATION.poolAnchor} 非協會證書，也不能直接換算其他平台分數。</small>`
  : `<p>新版引擎已完成強度／棋風分離，初步相鄰測試呈單調上升；目前樣本仍未達正式發布門檻，因此暫不顯示看似精確的站內 Elo。舊引擎數字已封存，不代表目前棋力。</p>`;

app.innerHTML = `
  <header class="site-header">
    <a class="brand" href="/" aria-label="雲端將族首頁">
      <span class="brand-mark">將</span>
      <span><strong>雲端將族</strong><small>Cloud Chess Guardians</small></span>
    </a>
    <div class="profile-tools">
      <button id="headerAvatarBtn" class="header-avatar-btn" type="button" data-change-avatar aria-label="選擇或更換我的角色">
        <img id="headerAvatarImage" alt="我的角色" /><span><small>我的角色</small><strong id="headerAvatarName"></strong></span>
      </button>
      <button id="leaderboardBtn" class="profile-summary" type="button" aria-label="查看排行榜">
        <span id="rankTitle">新進棋手</span>
        <strong id="profileScore">0</strong><small id="profileScoreLabel">名望</small>
      </button>
    </div>
  </header>

  <section class="story-banner" aria-label="${WORLD_STORY.title}">
    <img src="${WORLD_STORY.image}" alt="十二位守局人立於雲城棋路" />
    <div class="story-banner-shade"></div>
    <div class="story-banner-copy">
      <p class="eyebrow">THE TWELVE GAMES OF CLOUD GATE</p>
      <h1>${WORLD_STORY.title}</h1>
      <p>${WORLD_STORY.subtitle}</p>
      <div class="story-banner-actions"><button id="worldStoryBtn" type="button">展開雲城傳說 <span>→</span></button><button id="chessBoxBtn" type="button">查看棋匣 <span>◇</span></button><button id="homeLeaderboardBtn" type="button">棋士排行榜 <span>♜</span></button></div>
    </div>
  </section>

  <section class="journey-mode" aria-labelledby="journeyModeTitle">
    <div class="journey-mode-heading">
      <p class="eyebrow">CHOOSE YOUR PATH</p>
      <h2 id="journeyModeTitle">先選擇遊玩方式</h2>
      <p>想直接找任何高手切磋，或沿著雲門十二局逐關揭開故事。</p>
    </div>
    <div class="journey-mode-grid" role="group" aria-label="選擇遊玩模式">
      <button type="button" class="journey-mode-card free" data-game-mode="practice">
        <span class="journey-mode-seal">遊</span>
        <span><small>FREE PLAY</small><strong>自由模式</strong><em>十二位角色全部開放，可自由換人、悔棋與重賽，不計名望。</em></span>
        <b>選擇自由切磋 →</b>
      </button>
      <button type="button" class="journey-mode-card story" data-game-mode="challenge">
        <span class="journey-mode-seal">關</span>
        <span><small>STORY JOURNEY</small><strong>故事模式</strong><em>逐關揭露角色；可多次悔棋，每次扣本關所得的 20%。</em></span>
        <b>踏上雲門十二局 →</b>
      </button>
      <button type="button" id="resumeGameBtn" class="journey-mode-card resume" aria-describedby="resumeGameDetail">
        <span class="journey-mode-seal">續</span>
        <span><small>CONTINUE GAME</small><strong>接續上次棋局</strong><em id="resumeGameDetail">目前沒有未完成的棋局。</em></span>
        <b id="resumeGameAction">尚無可接續棋局</b>
      </button>
    </div>
  </section>

  <section id="selectionFlow" class="selection-flow" aria-live="polite" hidden>
    <button id="selectionBackBtn" class="selection-back" type="button">← 返回模式選擇</button>
    <div>
      <p id="selectionStepLabel" class="eyebrow">STEP 2 OF 3</p>
      <h1 id="selectionFlowTitle">選擇對手</h1>
      <p id="selectionFlowDescription">十二位守局人都已開放，選一位想切磋的對手。</p>
    </div>
  </section>

  <main class="game-layout">
    <aside class="opponents-panel panel" aria-labelledby="opponentsTitle">
      <div class="panel-heading">
        <p class="eyebrow">CHALLENGERS</p>
        <h2 id="opponentsTitle">選擇對手</h2>
      </div>
      <div id="opponentList" class="opponent-list"></div>
      <p class="level-note">沿著雲城棋路，依序遇見十二位性格與棋風迥異的守局人。</p>
    </aside>

    <aside class="arena-presence player-presence" aria-label="你的角色">
      <button id="leaveArenaBtn" class="arena-back" type="button" aria-label="返回對手介紹">← 對手介紹</button>
      <div class="presence-portrait"><img id="arenaPlayerImage" alt="你的角色" /></div>
      <p id="arenaPlayerSide" class="eyebrow">YOU・紅方</p><h2 id="arenaPlayerName"></h2><span id="arenaPlayerTitle"></span>
      <div id="playerReaction" class="reaction-bubble">請多指教。</div>
      <button id="changeAvatarBtn" class="presence-action" type="button" data-change-avatar>更換我的角色</button>
    </aside>

    <section class="board-column" aria-label="象棋對局">
      <div class="match-ribbon">
        <div><span class="turn-dot" id="turnDot"></span><span class="match-copy"><strong id="matchStatus" aria-live="polite">輪到你</strong><small id="matchDetail">紅方先行</small></span></div>
        <span id="checkStatus"></span>
      </div>
      <div class="mode-bar" aria-label="對局模式">
        <div class="mode-current"><small>本局模式</small><strong id="arenaModeName">自由模式</strong></div>
        <span id="modeRule"></span>
      </div>
      <div id="playHint" class="play-hint"><span>1</span>點選或拖曳你的棋子 <i>→</i><span>2</span>看亮點選落點 <i>→</i><span>3</span>留意電腦閃光回應</div>
      <div class="board-toolbar">
        <span>棋盤視角</span>
        <div class="board-style-switch" role="group" aria-label="選擇棋盤視角">
          <button type="button" data-board-style="2d">2D 平面</button>
          <button type="button" data-board-style="3d">3D 立體</button>
        </div>
        <button id="audioBtn" class="fullscreen-btn audio-btn" type="button" aria-haspopup="dialog" aria-label="聲音設定">🔊 聲音</button>
        <button id="matchInfoBtn" class="fullscreen-btn" type="button" aria-label="對局資訊">☰ 對局資訊</button>
        <button id="fullscreenBtn" class="fullscreen-btn" type="button" aria-label="切換全螢幕">⛶ 全螢幕</button>
        <small>點選或直接拖曳棋子</small>
      </div>
      <div id="boardFrame" class="board-frame" data-board-style="2d">
        <div class="board-surface" id="board" role="grid" aria-label="象棋棋盤">
          <svg class="board-lines" viewBox="0 0 900 1000" aria-hidden="true">
            <g>${horizontalLines}${verticalLines}
              <line x1="350" y1="50" x2="550" y2="250" /><line x1="550" y1="50" x2="350" y2="250" />
              <line x1="350" y1="750" x2="550" y2="950" /><line x1="550" y1="750" x2="350" y2="950" />
              <g class="position-marks">${positionMarks}</g>
            </g>
            <text x="250" y="520">楚 河</text><text x="650" y="520">漢 界</text>
          </svg>
          <div id="boardCells" class="board-cells"></div>
        </div>
        <div id="boardEvent" class="board-event" aria-live="assertive"></div>
      </div>
      <div class="primary-controls" aria-label="棋局控制">
        <button id="newGameBtn" class="control-btn primary"><span>＋</span>新局</button>
        <button id="undoBtn" class="control-btn"><span>↶</span>悔棋</button>
        <button id="saveBtn" class="control-btn"><span>▣</span>存讀檔</button>
        <button id="resignBtn" class="control-btn danger"><span>旗</span>認輸</button>
      </div>
    </section>

    <aside class="arena-presence rival-presence" aria-label="對手角色">
      <div class="presence-portrait"><img id="arenaRivalImage" alt="對手角色" /></div>
      <p id="arenaRivalChapter" class="eyebrow"></p><h2 id="arenaRivalName"></h2><span id="arenaRivalTitle"></span>
      <div id="rivalReaction" class="reaction-bubble">請。</div>
      <div id="arenaElo" class="arena-elo"></div>
    </aside>

    <aside class="info-panel panel">
      <section class="opponent-brief" id="opponentBrief"></section>
      <section class="captured-card" aria-label="被吃棋子">
        <div><span>你吃掉</span><div id="humanCaptures" class="captured-pieces"></div></div>
        <div><span>對手吃掉</span><div id="aiCaptures" class="captured-pieces"></div></div>
      </section>
      <section class="stats-card">
        <div class="panel-heading compact"><p class="eyebrow">YOUR RECORD</p><h2>你的戰績</h2></div>
        <div class="stats-grid">
          <div><strong id="winsStat">0</strong><span>勝</span></div>
          <div><strong id="drawsStat">0</strong><span>和</span></div>
          <div><strong id="lossesStat">0</strong><span>負</span></div>
        </div>
        <div id="masterBadge" class="master-badge"><span>一</span><p><strong>一級檢定章</strong><small>擊敗守關人後點亮</small></p></div>
      </section>
      <section class="history-card">
        <div class="history-heading"><h2>行棋紀錄</h2><span id="moveCount">0 手</span></div>
        <ol id="moveHistory" class="move-history"><li class="empty-history">開局待下</li></ol>
      </section>
      <button id="rulesBtn" class="text-button">規則與棋力說明 <span>→</span></button>
    </aside>
  </main>

  <footer><span>雲端將族・Cloud Chess Guardians v5.2</span><span>棋力與音效在瀏覽器內運作；排行榜成績同步至雲端</span></footer>

  <dialog id="saveDialog" class="app-dialog">
    <form method="dialog" class="dialog-card">
      <button class="dialog-close" value="cancel" aria-label="關閉">×</button>
      <p class="eyebrow">GAME ARCHIVE</p><h2>存讀棋局</h2>
      <div class="save-create"><input id="saveName" maxlength="40" placeholder="例如：對烈風的中炮局" /><button id="createSaveBtn" type="button">建立存檔</button></div>
      <div class="file-actions"><button id="exportBtn" type="button">匯出 JSON</button><button id="importBtn" type="button">匯入 JSON</button><input id="importInput" type="file" accept="application/json,.json" hidden /></div>
      <div id="saveList" class="save-list"></div>
      <p class="dialog-note">另有一份自動存檔，關閉頁面後會在下次自動續局。</p>
    </form>
  </dialog>

  <dialog id="rulesDialog" class="app-dialog">
    <form method="dialog" class="dialog-card rules-card">
      <button class="dialog-close" value="cancel" aria-label="關閉">×</button>
      <p class="eyebrow">HOUSE RULES</p><h2>規則與棋力說明</h2>
      <div class="rules-grid">
        <section><h3>完整合法走法</h3><p>包含蹩馬腿、塞象眼、炮架、九宮、過河兵，以及將帥不可照面。被將軍時只能走解除將軍的棋。</p></section>
        <section><h3>本版和棋</h3><p>同一局面含同一行棋方出現三次、六十回合未吃子，或只剩將帥士象時判和。競賽級長將／長捉裁決留待後續版本。</p></section>
        <section><h3>十二種棋風</h3><p>每位對手先依紅黑方使用自己的布局譜，例如中炮、屏風馬、仙人指路、飛相或仕角炮；走出譜外才交回 V1、V1-Pro 或 Pikafish。中盤仍只在合格候選內依攻殺、守城、惜子、殘局或均衡棋風重排。棋風不等於棋力，級分只採自動對弈結果。</p></section>
        <section><h3>自由與故事模式</h3><p>自由模式一開始便開放十二位角色，可自由換人、悔棋與重賽，不計名望。故事模式必須依序擊敗守關者，才會揭露下一位的肖像、姓名與故事；悔棋不限次數，但每次會扣除本關滿分的 20%，最低降至本場 0 分。</p></section>
        <section><h3>名望不是 Elo</h3><p>名望是遊戲進度與成就分數；站內 Elo 才是依對局結果估計的相對棋力。兩者分開計算。</p></section>
        <section class="elo-method"><h3>站內 Elo 校準</h3>${eloCalibrationDetails}</section>
        <section><h3>引擎與授權</h3><p>Pikafish、GPL 授權、NNUE 條款、完整對應原始碼與建置配方皆隨網站公開；NNUE 上游條款限制商業用途，目前定位為非商業使用。<a href="/engine/THIRD_PARTY_NOTICES.md" target="_blank" rel="noopener">查看第三方說明</a></p></section>
      </div>
      <button class="wide-button" value="cancel">知道了，回到棋局</button>
    </form>
  </dialog>
  <dialog id="audioDialog" class="app-dialog audio-dialog">
    <form method="dialog" class="dialog-card audio-card">
      <button class="dialog-close" value="cancel" aria-label="關閉">×</button>
      <p class="eyebrow">SOUND OF CLOUD GATE</p><h2>棋局聲音</h2>
      <label class="audio-setting"><span><strong>棋局音效</strong><small>落子、吃子、將軍與勝負提示</small></span><input id="effectsToggle" type="checkbox" /></label>
      <label class="audio-setting"><span><strong>角色語音</strong><small>十二位對手與說書人皆有角色聲線</small></span><input id="speechToggle" type="checkbox" /></label>
      <label class="audio-setting"><span><strong>背景音樂</strong><small>選角、對弈與結算各有三首無人聲配樂，每次隨機抽選且不連續重複</small></span><input id="musicToggle" type="checkbox" /></label>
      <label class="audio-volume"><span>音樂音量</span><input id="musicVolume" type="range" min="0" max="0.5" step="0.01" /></label>
      <fieldset class="speech-frequency"><legend>說話頻率</legend><label><input type="radio" name="speechFrequency" value="key" /> 關鍵時刻</label><label><input type="radio" name="speechFrequency" value="all" /> 每次落子</label></fieldset>
      <button id="previewVoiceBtn" type="button" class="wide-button">試聽目前角色</button>
      <p id="voiceSupportNote" class="dialog-note">語音由你的裝置即時合成，不上傳棋局；不同瀏覽器的音色可能略有差異。</p>
    </form>
  </dialog>
  <dialog id="matchInfoDialog" class="app-dialog match-info-dialog">
    <form method="dialog" class="dialog-card match-info-card">
      <button class="dialog-close" value="cancel" aria-label="關閉">×</button>
      <p class="eyebrow">MATCH DESK</p><h2>對局資訊</h2>
      <div class="match-info-tabs" role="tablist" aria-label="對局資訊分類">
        <button type="button" role="tab" data-match-tab="captures" aria-selected="true">吃子</button>
        <button type="button" role="tab" data-match-tab="history" aria-selected="false">棋譜</button>
        <button type="button" role="tab" data-match-tab="rules" aria-selected="false">規則</button>
      </div>
      <section class="match-tab-panel active" data-match-panel="captures"><div id="matchCaptures"></div></section>
      <section class="match-tab-panel" data-match-panel="history"><ol id="matchHistory" class="move-history"></ol></section>
      <section class="match-tab-panel" data-match-panel="rules">
        <div class="quick-rules"><p><strong>走子：</strong>每局隨機分配紅黑方；點選或拖曳你的棋子，再選擇亮起的合法落點。</p><p><strong>和棋：</strong>三次重複局面、六十回合未吃子，或雙方子力不足。</p><p><strong>故事模式：</strong>對手鎖定；悔棋不限次數，每次扣本關所得的 20%，最低為 0 分。</p></div>
        <button id="matchFullRulesBtn" type="button" class="wide-button">查看完整規則與棋力</button>
      </section>
    </form>
  </dialog>
  <dialog id="chessBoxDialog" class="app-dialog chess-box-dialog">
    <form method="dialog" class="dialog-card chess-box-card">
      <button class="dialog-close" value="cancel" aria-label="關閉">×</button>
      <p class="eyebrow">THE NAMELESS CHESS BOX</p><h2>無名棋匣</h2>
      <p class="chess-box-intro">每在故事模式擊敗一位守局人，匣底便浮現一段舊刻。</p>
      <div id="inscriptionGrid" class="inscription-grid"></div>
      <div id="completedInscription" class="completed-inscription"></div>
    </form>
  </dialog>
  <dialog id="storyDialog" class="app-dialog story-dialog">
    <article class="dialog-card story-card">
      <button class="dialog-close" data-close-story aria-label="關閉">×</button>
      <div class="story-portrait-wrap"><img id="storyPortrait" src="${WORLD_STORY.image}" alt="" /></div>
      <div class="story-dialog-copy">
        <p id="storyChapter" class="eyebrow">${WORLD_STORY.title}</p>
        <h2 id="storyName">${WORLD_STORY.title}</h2>
        <p id="storyEpithet" class="story-epithet">${WORLD_STORY.subtitle}</p>
        <p id="storyBackstory">${WORLD_STORY.intro}</p>
        <section id="storyEncounter" class="story-note"></section>
        <section id="storyLesson" class="story-lesson"></section>
        <button class="wide-button" data-close-story>回到棋局</button>
      </div>
    </article>
  </dialog>
  <dialog id="avatarDialog" class="app-dialog avatar-dialog">
    <form method="dialog" class="dialog-card avatar-card">
      <button class="dialog-close" value="cancel" aria-label="關閉">×</button>
      <p class="eyebrow">CREATE YOUR CHESS SELF</p><h2>找出你的雲城形象</h2>
      <div class="avatar-quiz">
        <fieldset><legend>你想使用哪一組形象？</legend><label><input type="radio" name="avatarGender" value="female" checked /> 女棋士</label><label><input type="radio" name="avatarGender" value="male" /> 男棋士</label></fieldset>
        <fieldset><legend>空閒時，你比較喜歡？</legend><label><input type="radio" name="avatarInterest" value="strategy" checked /> 閱讀與思考</label><label><input type="radio" name="avatarInterest" value="outdoors" /> 旅行與戶外</label><label><input type="radio" name="avatarInterest" value="craft" /> 美食與手作</label><label><input type="radio" name="avatarInterest" value="arts" /> 音樂與影像</label></fieldset>
        <fieldset><legend>你偏好的角色輪廓？</legend><label><input type="radio" name="avatarBuild" value="light" checked /> 輕捷修長</label><label><input type="radio" name="avatarBuild" value="solid" /> 沉穩厚實</label></fieldset>
      </div>
      <div class="avatar-result"><img id="avatarPreview" alt="配對出的玩家形象" /><div><small>為你配對</small><strong id="avatarPreviewName"></strong><span id="avatarPreviewTitle"></span></div></div>
      <button id="useAvatarBtn" type="button" class="wide-button">使用這個形象</button>
    </form>
  </dialog>
  <dialog id="resultDialog" class="app-dialog result-dialog">
    <form method="dialog" class="dialog-card result-card">
      <button class="dialog-close" value="cancel" aria-label="關閉">×</button>
      <div id="resultSeal" class="result-seal">勝</div>
      <p id="resultEyebrow" class="eyebrow">CHALLENGE COMPLETE</p>
      <h2 id="resultTitle">本局獲勝</h2>
      <p id="resultReason" class="result-reason"></p>
      <div id="resultCharacter" class="result-character"><div class="result-portrait-wrap"><img id="resultPortrait" alt="" /><span id="resultPortraitLabel"></span></div><blockquote id="resultStoryQuote"></blockquote></div>
      <div id="resultFacts" class="result-facts"></div>
      <section id="resultLessonReview" class="result-lesson-review" aria-labelledby="resultLessonTitle">
        <div class="result-lesson-heading"><span>本局命題回顧</span><b id="resultLessonVerdict">分析中</b></div>
        <h3 id="resultLessonTitle"></h3>
        <p id="resultLessonSummary"></p>
        <div id="resultLessonMetrics" class="result-lesson-metrics"></div>
        <p id="resultLessonHighlight" class="result-lesson-highlight"></p>
      </section>
      <p id="resultReward" class="result-reward"></p>
      <p id="onlineResultDelta" class="online-result-delta"></p>
      <div class="result-actions">
        <button id="rematchBtn" type="button" class="wide-button">與同一位再戰</button>
        <button id="nextOpponentBtn" type="button" class="secondary-action">挑戰下一位</button>
      </div>
    </form>
  </dialog>

  <dialog id="confirmDialog" class="app-dialog confirm-dialog">
    <form method="dialog" class="dialog-card">
      <p class="eyebrow">PLEASE CONFIRM</p><h2 id="confirmTitle">確認操作</h2>
      <p id="confirmMessage" class="confirm-message"></p>
      <div class="confirm-actions"><button value="cancel" class="secondary-action">先不做</button><button id="confirmActionBtn" type="button" class="wide-button">確定</button></div>
    </form>
  </dialog>
  <dialog id="playerNameDialog" class="app-dialog player-name-dialog">
    <form id="playerNameForm" class="dialog-card player-name-card">
      <p class="eyebrow">ENTER THE CLOUD GATE</p>
      <div id="accountModeTabs" class="account-mode-tabs" role="tablist" aria-label="棋士帳號操作">
        <button type="button" data-account-mode="register" aria-selected="true">建立帳號</button>
        <button type="button" data-account-mode="login" aria-selected="false">已有帳號登入</button>
      </div>
      <h2 id="playerAccountTitle">建立棋士帳號</h2>
      <p id="playerAccountDescription">設定名字與密碼後，就能在手機、電腦或其他瀏覽器接續同一份成績。</p>
      <label for="playerNameInput">棋士名字</label>
      <input id="playerNameInput" name="displayName" minlength="2" maxlength="16" autocomplete="username" placeholder="例如：青禾棋客" required />
      <div id="playerPasswordFields" class="account-password-fields">
        <label for="playerPasswordInput">密碼</label>
        <input id="playerPasswordInput" name="password" type="password" minlength="4" maxlength="128" autocomplete="new-password" placeholder="至少 4 個字元，不限格式" />
        <div id="playerPasswordConfirmField">
          <label for="playerPasswordConfirmInput">再次輸入密碼</label>
          <input id="playerPasswordConfirmInput" name="passwordConfirm" type="password" minlength="4" maxlength="128" autocomplete="new-password" placeholder="再輸入一次密碼" />
        </div>
      </div>
      <p id="playerNameError" class="form-error" role="alert"></p>
      <button id="savePlayerNameBtn" type="submit" class="wide-button">建立帳號並進入</button>
      <button id="offlinePlayBtn" type="button" class="text-button">暫時離線遊玩</button>
      <small id="playerAccountNote">密碼只會以加鹽雜湊保存；目前未綁定 Email，請自行妥善保管密碼。</small>
    </form>
  </dialog>
  <dialog id="leaderboardDialog" class="app-dialog leaderboard-dialog">
    <article class="dialog-card leaderboard-card">
      <button class="dialog-close" type="button" data-close-leaderboard aria-label="關閉">×</button>
      <p class="eyebrow">CLOUD GATE SCOREBOARD</p><h2>雲門棋士榜</h2>
      <section id="leaderboardMe" class="leaderboard-me"></section>
      <div class="leaderboard-tabs" role="tablist" aria-label="排行榜類型">
        <button type="button" data-leaderboard-tab="overall" aria-selected="true">綜合榜</button>
        <button type="button" data-leaderboard-tab="story" aria-selected="false">闖關榜</button>
        <button type="button" data-leaderboard-tab="free" aria-selected="false">自由榜</button>
      </div>
      <div id="leaderboardStatus" class="leaderboard-status">正在讀取排行榜…</div>
      <ol id="leaderboardList" class="leaderboard-list"></ol>
      <details class="score-rules">
        <summary>分數怎麼算？</summary>
        <p><strong>故事模式：</strong>每關只計最佳成績。和棋先取得部分分數，之後獲勝只補到該關滿分；每次悔棋扣本關所得的 20%，最多扣到本場 0 分，日後以較少悔棋重過仍可補回差額。</p>
        <p><strong>自由模式：</strong>從 0 切磋分開始，依對手強弱與勝負升降；打贏強手加得多，輸給弱手扣得多，最低維持 0 分。</p>
        <p><strong>綜合分：</strong>從 0 分起跳，合併闖關分與自由切磋分。三張榜分開保留，不會只看單一玩法。</p>
      </details>
      <div class="leaderboard-actions"><button id="accountSettingsBtn" type="button" class="secondary-action">帳號設定</button><button type="button" class="wide-button" data-close-leaderboard>回到雲門</button></div>
    </article>
  </dialog>
  <div id="toast" class="toast" role="status" aria-live="polite"></div>
`;

function query<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`找不到介面元件 ${selector}`);
  return element;
}

const boardCells = query<HTMLDivElement>('#boardCells');
const boardFrame = query<HTMLDivElement>('#boardFrame');
const opponentList = query<HTMLDivElement>('#opponentList');
const saveDialog = query<HTMLDialogElement>('#saveDialog');
const rulesDialog = query<HTMLDialogElement>('#rulesDialog');
const audioDialog = query<HTMLDialogElement>('#audioDialog');
const matchInfoDialog = query<HTMLDialogElement>('#matchInfoDialog');
const chessBoxDialog = query<HTMLDialogElement>('#chessBoxDialog');
const storyDialog = query<HTMLDialogElement>('#storyDialog');
const avatarDialog = query<HTMLDialogElement>('#avatarDialog');
const resultDialog = query<HTMLDialogElement>('#resultDialog');
const confirmDialog = query<HTMLDialogElement>('#confirmDialog');
const playerNameDialog = query<HTMLDialogElement>('#playerNameDialog');
const leaderboardDialog = query<HTMLDialogElement>('#leaderboardDialog');
const saveList = query<HTMLDivElement>('#saveList');
const saveName = query<HTMLInputElement>('#saveName');
const importInput = query<HTMLInputElement>('#importInput');
const toast = query<HTMLDivElement>('#toast');

const restored = loadAutosave();
let state: GameState = restored?.state ?? createInitialGame();
let difficulty: Difficulty = restored?.difficulty ?? 'beginner';
let boardStyle: BoardStyle = restored?.boardStyle ?? '2d';
let gameMode: GameMode = restored?.gameMode ?? 'practice';
let undosUsed = restored?.undosUsed ?? 0;
let playerSide: Side = restored?.playerSide ?? randomSide();
let profile: PlayerProfile = loadProfile();
if (!isOpponentAvailable(gameMode, profile, difficulty)) {
  difficulty = highestRevealedDifficulty(profile);
  state = createInitialGame();
  undosUsed = 0;
}
let selected: number | null = null;
let undoStack: GameState[] = [];
let thinking = false;
let worker: Worker | null = null;
let workerBackend: EngineBackend | null = null;
let pikafishReady = false;
let pikafishPrewarming = false;
let pikafishCheckoutTimer = 0;
let homePrewarmTimer = 0;
let jobId = 0;
let aiWatchdogTimer = 0;
let bookMoveTimer = 0;
let toastTimer = 0;
let suppressClickUntil = 0;
let confirmAction: (() => void) | null = null;
let lastResultGameId: string | null = null;
let activeMovePly: number | null = null;
let moveCue: { actor: 'human' | 'ai'; move: MoveRecord } | null = null;
let moveCueTimer = 0;
let movePulseTimer = 0;
let lastSearchSummary = '';
let lastEngineUsed: SearchResult['engine'] | null = null;
let arenaActive = false;
type SelectionStep = 'mode' | 'opponents' | 'intro';
let selectionStep: SelectionStep = 'mode';
let lastAutoNarrationKey: Difficulty | null = null;
let playerAvatar: PlayerAvatar = PLAYER_AVATARS.find((avatar) => avatar.id === localStorage.getItem('cloud-xiangqi-player-avatar')) ?? PLAYER_AVATARS[0]!;
let reactionTimer = 0;
let onlineIdentity: OnlineIdentity | null = loadOnlineIdentity();
let onlinePlayer: LeaderboardPlayer | null = null;
let leaderboardSnapshot: LeaderboardSnapshot | null = null;
let cloudSyncTimer = 0;
let lastCloudSignature = '';
let localStateUpdatedAt = restored && restored.state.gameId === state.gameId
  ? Date.parse(restored.savedAt) || 0
  : Date.now();
let leaderboardTab: keyof LeaderboardSnapshot['leaderboards'] = 'overall';
type AccountDialogMode = 'register' | 'login' | 'edit' | 'upgrade';
let accountDialogMode: AccountDialogMode = 'register';
let passwordUpgradePrompted = false;
const pendingOnlineGames = new Set<string>();

interface DragState {
  pointerId: number;
  from: number;
  originX: number;
  originY: number;
  dragging: boolean;
  ghost: HTMLElement | null;
  over: number | null;
}

let dragState: DragState | null = null;

function oppositeSide(side: Side): Side {
  return side === 'red' ? 'black' : 'red';
}

function randomSide(): Side {
  const value = new Uint8Array(1);
  crypto.getRandomValues(value);
  return value[0]! % 2 === 0 ? 'red' : 'black';
}

function ensureOnlineMatchForCurrentGame(): void {
  if (!onlineIdentity || state.moves.length > 0 || state.outcome !== 'ongoing') return;
  void beginOnlineMatch({ gameId: state.gameId, mode: gameMode, difficulty, playerSide }).catch(() => {
    // Offline play remains available. A missing ticket is explained if the
    // player later attempts to submit this game to the leaderboard.
  });
}

function cloudStateSignature(): string {
  return onlineIdentity
    ? `${onlineIdentity.playerId}:${state.gameId}:${state.moves.length}:${state.outcome}:${difficulty}:${gameMode}:${undosUsed}:${playerSide}:${boardStyle}`
    : '';
}

function scheduleCloudAutosave(): void {
  window.clearTimeout(cloudSyncTimer);
  const identity = onlineIdentity;
  if (!identity) return;
  const signature = cloudStateSignature();
  if (signature === lastCloudSignature) return;
  const snapshot = isResumableGame(state)
    ? serializeGame(state, difficulty, boardStyle, '雲端續局', gameMode, undosUsed, playerSide)
    : null;
  cloudSyncTimer = window.setTimeout(async () => {
    if (onlineIdentity?.playerId !== identity.playerId) return;
    try {
      const synced = snapshot
        ? await saveCloudGame(snapshot, identity)
        : await deleteCloudGame(identity);
      if (synced && cloudStateSignature() === signature) lastCloudSignature = signature;
    } catch {
      // Local autosave remains authoritative while offline; the next move retries.
    }
  }, 900);
}

function saveCurrentState(): void {
  localStateUpdatedAt = Date.now();
  saveAutosave(state, difficulty, boardStyle, gameMode, undosUsed, playerSide);
  scheduleCloudAutosave();
}

async function restoreCloudGame(): Promise<void> {
  const identity = onlineIdentity;
  if (!identity) return;
  try {
    const cloud = await loadCloudGame(identity);
    if (onlineIdentity?.playerId !== identity.playerId) return;
    if (!cloud) {
      scheduleCloudAutosave();
      return;
    }
    const loaded = deserializeGame(cloud.game);
    if (!isResumableGame(loaded.state)) return;
    const portable = cloud.onlineMatchTicket
      ? { ...cloud.game, onlineMatchTicket: cloud.onlineMatchTicket }
      : cloud.game;
    const activateCloudGame = () => {
      lastCloudSignature = '';
      loadStored(portable);
      showToast(`已從雲端接續與${PERSONALITIES[loaded.difficulty].name}的棋局`);
    };
    if (!isResumableGame(state)) {
      activateCloudGame();
      return;
    }
    const cloudUpdatedAt = Date.parse(cloud.updatedAt) || Date.parse(loaded.savedAt) || 0;
    if (loaded.state.gameId === state.gameId) {
      if (cloudUpdatedAt > localStateUpdatedAt) activateCloudGame();
      else scheduleCloudAutosave();
      return;
    }
    askConfirmation(
      '雲端另有未完成棋局',
      `雲端保存了與${PERSONALITIES[loaded.difficulty].name}的 ${loaded.state.moves.length} 手棋局；要在這台裝置接著下嗎？`,
      '載入雲端棋局',
      activateCloudGame,
    );
  } catch {
    scheduleCloudAutosave();
  }
}

function schedulePikafishCheckout(): void {
  window.clearTimeout(pikafishCheckoutTimer);
  pikafishCheckoutTimer = window.setTimeout(() => {
    const highLevelIntro = selectionStep === 'intro' && preferredBackend(difficulty) === 'pikafish';
    if (arenaActive || thinking || highLevelIntro || workerBackend !== 'pikafish') {
      if (workerBackend === 'pikafish') schedulePikafishCheckout();
      return;
    }
    worker?.terminate();
    worker = null;
    workerBackend = null;
    pikafishReady = false;
    pikafishPrewarming = false;
    lastSearchSummary = '進階棋力已釋放；下載快取仍保留';
  }, 180_000);
}

function prewarmPikafish(fromHome = false): void {
  if ((!fromHome && preferredBackend(difficulty) !== 'pikafish') || !supportsPikafishRuntime()) return;
  if (workerBackend === 'pikafish' || thinking || worker) return;
  const warmupWorker = createSearchWorker('pikafish');
  worker = warmupWorker;
  workerBackend = 'pikafish';
  pikafishPrewarming = true;
  pikafishReady = false;
  schedulePikafishCheckout();
  warmupWorker.onmessage = (event: MessageEvent<SearchWorkerMessage>) => {
    if (worker !== warmupWorker) return;
    if (event.data.type === 'status') {
      lastSearchSummary = event.data.message;
      return;
    }
    if (event.data.type === 'ready') {
      pikafishPrewarming = false;
      pikafishReady = true;
      lastSearchSummary = '進階棋力已預先就緒';
      schedulePikafishCheckout();
      return;
    }
    if (event.data.type === 'error') {
      warmupWorker.terminate();
      if (worker === warmupWorker) {
        worker = null;
        workerBackend = null;
      }
      pikafishPrewarming = false;
      pikafishReady = false;
    }
  };
  warmupWorker.onerror = () => {
    warmupWorker.terminate();
    if (worker === warmupWorker) {
      worker = null;
      workerBackend = null;
    }
    pikafishPrewarming = false;
    pikafishReady = false;
  };
}

function scheduleHomePikafishPrewarm(): void {
  const connection = (navigator as Navigator & {
    connection?: { saveData?: boolean; effectiveType?: string };
  }).connection;
  if (connection?.saveData || connection?.effectiveType === 'slow-2g' || connection?.effectiveType === '2g') return;
  window.clearTimeout(homePrewarmTimer);
  homePrewarmTimer = window.setTimeout(() => prewarmPikafish(true), 1_200);
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>'"]/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' })[char] ?? char);
}

function showToast(message: string): void {
  toast.textContent = message;
  toast.classList.add('show');
  window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => toast.classList.remove('show'), 2600);
}

function renderLeaderboard(): void {
  const me = onlinePlayer ?? leaderboardSnapshot?.me ?? null;
  const meCard = query<HTMLElement>('#leaderboardMe');
  meCard.innerHTML = me
    ? `<div><small>你的棋士</small><strong>${escapeHtml(me.displayName)}</strong></div><div><span><small>綜合</small><b>${me.overallScore.toLocaleString('zh-TW')}</b></span><span><small>闖關名望</small><b>${me.storyReputation.toLocaleString('zh-TW')}</b></span><span><small>自由切磋</small><b>${me.freeRating.toLocaleString('zh-TW')}</b></span></div>`
    : '<p>填寫棋士名字後，就能把正式對局成績送上排行榜。</p>';
  document.querySelectorAll<HTMLButtonElement>('[data-leaderboard-tab]').forEach((button) => {
    const active = button.dataset.leaderboardTab === leaderboardTab;
    button.classList.toggle('active', active);
    button.setAttribute('aria-selected', String(active));
  });
  const list = leaderboardSnapshot?.leaderboards[leaderboardTab] ?? [];
  query<HTMLOListElement>('#leaderboardList').innerHTML = list.length
    ? list.map((player, index) => {
      const primary = leaderboardTab === 'overall'
        ? player.overallScore
        : leaderboardTab === 'story'
          ? player.storyReputation
          : player.freeRating;
      const detail = leaderboardTab === 'story'
        ? `${player.clears} 關・${player.storyWins} 勝`
        : leaderboardTab === 'free'
          ? `${player.freeGames} 局・${player.freeWins} 勝 ${player.freeDraws} 和`
          : `闖關 ${player.storyReputation.toLocaleString('zh-TW')}・自由 ${player.freeRating}`;
      return `<li class="${player.id === onlineIdentity?.playerId ? 'is-me' : ''}"><span class="leaderboard-rank">${index + 1}</span><span class="leaderboard-name">${escapeHtml(player.displayName)}<small>${detail}</small></span><strong>${primary.toLocaleString('zh-TW')}</strong></li>`;
    }).join('')
    : '<li class="empty-leaderboard">這張榜還沒有完成對局的棋士。</li>';
}

function applyOnlineProgress(player: LeaderboardPlayer): void {
  profile.score = Math.max(profile.score, player.storyReputation);
  profile.scoreBasis = 'zero';
  profile.wins = Math.max(profile.wins, player.storyWins);
  profile.draws = Math.max(profile.draws, player.storyDraws);
  profile.losses = Math.max(profile.losses, player.storyLosses);
  for (const id of DIFFICULTY_ORDER) {
    const rank = player.storyProgress?.[id];
    if (rank === 2) profile.opponentRecords[id].wins = Math.max(1, profile.opponentRecords[id].wins);
    else if (rank === 1) profile.opponentRecords[id].draws = Math.max(1, profile.opponentRecords[id].draws);
    else if (rank === 0 && Object.hasOwn(player.storyProgress ?? {}, id)) {
      profile.opponentRecords[id].losses = Math.max(1, profile.opponentRecords[id].losses);
    }
  }
  if (player.storyProgress?.master === 2) profile.masterWins = Math.max(1, profile.masterWins);
  saveProfile(profile);
}

async function refreshLeaderboard(showErrors = true): Promise<void> {
  query<HTMLElement>('#leaderboardStatus').textContent = '正在讀取排行榜…';
  try {
    leaderboardSnapshot = await fetchLeaderboard(onlineIdentity);
    onlinePlayer = leaderboardSnapshot.me;
    if (onlinePlayer) applyOnlineProgress(onlinePlayer);
    query<HTMLElement>('#leaderboardStatus').textContent = `顯示前 ${leaderboardSnapshot.leaderboards[leaderboardTab].length} 名・剛剛更新`;
    renderProfile();
    renderLeaderboard();
    if (onlineIdentity && onlinePlayer && onlinePlayer.hasPassword === false && !passwordUpgradePrompted) {
      passwordUpgradePrompted = true;
      window.setTimeout(() => openPlayerNameDialog('upgrade'), 350);
    }
  } catch (error) {
    query<HTMLElement>('#leaderboardStatus').textContent = error instanceof Error ? error.message : '排行榜暫時無法讀取';
    renderLeaderboard();
    if (showErrors) showToast('排行榜暫時無法連線，棋局仍可離線進行');
  }
}

function openLeaderboard(): void {
  renderLeaderboard();
  if (!leaderboardDialog.open) leaderboardDialog.showModal();
  void refreshLeaderboard();
}

function openPlayerNameDialog(mode: AccountDialogMode = onlineIdentity
  ? onlinePlayer?.hasPassword === false ? 'upgrade' : 'edit'
  : 'register'): void {
  accountDialogMode = mode;
  const isAccountEntry = mode === 'register' || mode === 'login';
  const isPasswordMode = mode !== 'edit';
  const needsConfirmation = mode === 'register' || mode === 'upgrade';
  const titles: Record<AccountDialogMode, string> = {
    register: '建立棋士帳號', login: '登入棋士帳號', edit: '更改棋士名字', upgrade: '讓棋士帳號跨裝置',
  };
  const descriptions: Record<AccountDialogMode, string> = {
    register: '設定名字與密碼後，就能在手機、電腦或其他瀏覽器接續同一份成績。',
    login: '在新裝置輸入原本的棋士名字與密碼，即可接續排行榜分數與戰績。',
    edit: '更改公開顯示的棋士名字；你的分數、戰績與已解鎖角色都會保留。',
    upgrade: '你目前是舊版瀏覽器帳號。設定密碼後，就能在其他手機或電腦登入。',
  };
  const submitLabels: Record<AccountDialogMode, string> = {
    register: '建立帳號並進入', login: '登入並接續進度', edit: '儲存新名字', upgrade: '設定密碼並啟用跨裝置',
  };
  query<HTMLElement>('#accountModeTabs').hidden = !isAccountEntry;
  document.querySelectorAll<HTMLButtonElement>('[data-account-mode]').forEach((button) => {
    const selected = button.dataset.accountMode === mode;
    button.setAttribute('aria-selected', String(selected));
    button.classList.toggle('active', selected);
  });
  query<HTMLElement>('#playerAccountTitle').textContent = titles[mode];
  query<HTMLElement>('#playerAccountDescription').textContent = descriptions[mode];
  const nameInput = query<HTMLInputElement>('#playerNameInput');
  nameInput.value = mode === 'register' ? '' : onlineIdentity?.displayName ?? '';
  nameInput.readOnly = mode === 'upgrade';
  query<HTMLElement>('#playerPasswordFields').hidden = !isPasswordMode;
  query<HTMLElement>('#playerPasswordConfirmField').hidden = !needsConfirmation;
  const passwordInput = query<HTMLInputElement>('#playerPasswordInput');
  passwordInput.value = '';
  passwordInput.autocomplete = mode === 'login' ? 'current-password' : 'new-password';
  query<HTMLInputElement>('#playerPasswordConfirmInput').value = '';
  query<HTMLButtonElement>('#savePlayerNameBtn').textContent = submitLabels[mode];
  const secondary = query<HTMLButtonElement>('#offlinePlayBtn');
  secondary.textContent = mode === 'edit' ? '取消' : mode === 'upgrade' ? '稍後設定' : '暫時離線遊玩';
  query<HTMLElement>('#playerAccountNote').textContent = mode === 'login'
    ? '這台裝置會取得自己的登入憑證，不會讓其他已登入的裝置登出。'
    : mode === 'edit'
      ? '棋士名字不可與其他玩家重複。'
      : '密碼只會以加鹽雜湊保存；目前未綁定 Email，請自行妥善保管密碼。';
  query<HTMLElement>('#playerNameError').textContent = '';
  if (!playerNameDialog.open) playerNameDialog.showModal();
  window.setTimeout(() => (mode === 'upgrade' ? passwordInput : nameInput).focus(), 80);
}

async function syncOnlineResult(reward: OutcomeReward): Promise<void> {
  if (!onlineIdentity || pendingOnlineGames.has(state.gameId)) return;
  pendingOnlineGames.add(state.gameId);
  try {
    const result = await submitOnlineResult({
      gameId: state.gameId,
      mode: gameMode,
      difficulty,
      result: reward.result,
      moveTrace: state.moves.map(({ from, to }) => ({ from, to })),
      undosUsed,
      playerSide,
      resigned: state.outcomeReason === '你已認輸',
    });
    if (!result) return;
    onlinePlayer = result.player;
    applyOnlineProgress(result.player);
    leaderboardSnapshot = null;
    renderProfile();
    const delta = query<HTMLElement>('#onlineResultDelta');
    delta.textContent = gameMode === 'challenge'
      ? result.delta > 0
        ? `線上闖關名望 +${result.delta}・已登上棋士榜`
        : '本關最佳成績未提升・對局紀錄已更新'
      : `自由切磋分 ${result.delta >= 0 ? '+' : ''}${result.delta}・目前 ${result.player.freeRating}`;
    delta.classList.add('show');
  } catch (reason) {
    const message = reason instanceof Error ? reason.message : '排行榜服務暫時無法使用';
    query<HTMLElement>('#onlineResultDelta').textContent = `線上成績未登記：${message}；本機紀錄不受影響`;
  } finally {
    pendingOnlineGames.delete(state.gameId);
  }
}

function renderAudioControls(): void {
  const settings = gameAudio.getSettings();
  const effectsAvailable = gameAudio.effectsAvailable();
  const speechAvailable = gameAudio.speechAvailable();
  const musicAvailable = gameAudio.musicAvailable();
  const hasEnabledAudio = (effectsAvailable && settings.effects) || (speechAvailable && settings.speech) || (musicAvailable && settings.music);
  query<HTMLButtonElement>('#audioBtn').textContent = effectsAvailable || speechAvailable || musicAvailable
    ? (hasEnabledAudio ? '🔊 聲音' : '🔇 靜音')
    : '🔇 無聲';
  query<HTMLButtonElement>('#audioBtn').classList.toggle('audio-muted', !hasEnabledAudio);
  query<HTMLInputElement>('#effectsToggle').checked = settings.effects;
  query<HTMLInputElement>('#effectsToggle').disabled = !effectsAvailable;
  query<HTMLInputElement>('#speechToggle').checked = settings.speech;
  query<HTMLInputElement>('#speechToggle').disabled = !speechAvailable;
  query<HTMLInputElement>('#musicToggle').checked = settings.music;
  query<HTMLInputElement>('#musicToggle').disabled = !musicAvailable;
  query<HTMLInputElement>('#musicVolume').value = String(settings.musicVolume);
  query<HTMLInputElement>('#musicVolume').disabled = !settings.music || !musicAvailable;
  document.querySelectorAll<HTMLInputElement>('input[name="speechFrequency"]').forEach((input) => {
    input.checked = input.value === settings.speechFrequency;
    input.disabled = !settings.speech || !speechAvailable;
  });
  query<HTMLButtonElement>('#previewVoiceBtn').disabled = !settings.speech || !speechAvailable;
  query<HTMLElement>('#voiceSupportNote').textContent = effectsAvailable && speechAvailable && musicAvailable
    ? '說書人、角色語音與九首配樂皆已預先製作；遊玩時不呼叫外部 API，也不上傳棋局。'
    : effectsAvailable
      ? '這個瀏覽器沒有提供裝置端語音；棋局音效仍可正常使用。'
      : speechAvailable
        ? '這個瀏覽器沒有提供棋局音效；角色語音仍可正常使用。'
        : '這個瀏覽器沒有提供音效或裝置端語音，畫面提示仍會完整保留。';
}

function speakOpponent(cue: VoiceCue, message: string, important = true): void {
  gameAudio.speak(difficulty, cue, message, important);
}

function rankTitle(score: number): string {
  if (profile.masterWins > 0) return '一級守關者';
  if (score >= 1200) return '將族好手';
  if (score >= 500) return '進階棋士';
  if (score >= 100) return '切磋棋友';
  return '新進棋手';
}

function outcomeText(): string {
  if (state.outcome === `${playerSide}_win`) return `你贏了・${state.outcomeReason}`;
  if (state.outcome === `${oppositeSide(playerSide)}_win`) return `對手獲勝・${state.outcomeReason}`;
  if (state.outcome === 'draw') return `和棋・${state.outcomeReason}`;
  if (thinking) return `${PERSONALITIES[difficulty].name}思考中…`;
  return state.sideToMove === playerSide ? '輪到你' : '對手準備落子';
}

function renderOpponents(): void {
  const matchLocked = !canChangeOpponent(gameMode, state);
  opponentList.innerHTML = DIFFICULTY_ORDER.map((id, index) => {
    const opponent = PERSONALITIES[id];
    const story = OPPONENT_STORIES[opponent.id];
    const record = profile.opponentRecords[opponent.id];
    const met = record.wins + record.draws + record.losses > 0;
    const revealed = isOpponentAvailable(gameMode, profile, opponent.id);
    const unavailable = !revealed || (matchLocked && opponent.id !== difficulty);
    if (!revealed) return `
    <button class="opponent-card story-locked" data-difficulty="${opponent.id}" aria-disabled="true" aria-label="第 ${index + 1} 位守關者尚未揭露">
      <span class="opponent-avatar mystery-avatar" aria-hidden="true"><b>？</b></span>
      <span class="opponent-copy"><small>第 ${index + 1} 局・未揭之路</small><strong>尚未現身</strong><em>擊敗上一位守關者後揭露</em></span>
      <span class="select-mark">鎖</span>
    </button>`;
    return `
    <button class="opponent-card ${opponent.id === difficulty ? 'active' : ''} ${unavailable && opponent.id !== difficulty ? 'locked' : ''}" data-difficulty="${opponent.id}" aria-pressed="${opponent.id === difficulty}" aria-disabled="${unavailable && opponent.id !== difficulty}">
      <span class="opponent-avatar"><img src="${story.image}" alt="" loading="lazy" /><i>${opponent.avatar}</i></span>
      <span class="opponent-copy"><small>${story.chapter}・${story.location}</small><strong>${opponent.name}</strong><em>${story.epithet}${met ? '・曾經交手' : ''}</em></span>
      <span class="select-mark">${opponent.id === difficulty ? '✓' : '›'}</span>
    </button>
  `; }).join('');
  const opponent = PERSONALITIES[difficulty];
  const record = profile.opponentRecords[difficulty];
  const lines = OPPONENT_LINES[difficulty];
  const story = OPPONENT_STORIES[difficulty];
  const lastMove = state.moves.at(-1);
  const comment = thinking
    ? lines.thinking
    : !lastMove
      ? lines.opening
      : lastMove.piece.side === oppositeSide(playerSide)
        ? (lastMove.captured ? lines.capture : lines.reply)
        : '這一步我看到了，讓我想想。';
  query<HTMLElement>('#opponentBrief').innerHTML = `
    <div class="brief-avatar ${thinking ? 'thinking' : ''}"><img src="${story.image}" alt="${opponent.name}肖像" /></div><div><p class="eyebrow">${story.chapter}・${story.location}</p><h2>${opponent.name}<small>${story.epithet}</small></h2><p class="character-backstory">${story.backstory}</p><p class="character-encounter"><strong>與你相遇</strong>${story.encounter}</p><p class="character-hidden-past"><strong>未完的舊事</strong>${story.hiddenPast}</p><p class="character-lesson"><strong>這一局</strong>${story.lesson}</p><blockquote>${comment}</blockquote><div class="brief-actions"><button class="narrator-button" type="button" data-narrate-opponent>◉ 聽說書人介紹</button><button class="start-match" type="button" data-start-match>進入對局 <span>→</span></button></div><em>你們的對局 ${record.wins} 勝 ${record.draws} 和 ${record.losses} 負</em></div>
  `;
}

function setReaction(target: 'player' | 'rival', message: string, mood: 'calm' | 'capture' | 'check' | 'loss' = 'calm'): void {
  const element = query<HTMLElement>(target === 'player' ? '#playerReaction' : '#rivalReaction');
  const presence = element.closest<HTMLElement>('.arena-presence');
  element.textContent = message;
  presence?.classList.remove('react-capture', 'react-check', 'react-loss');
  if (mood !== 'calm') presence?.classList.add(`react-${mood}`);
  window.clearTimeout(reactionTimer);
  reactionTimer = window.setTimeout(() => presence?.classList.remove('react-capture', 'react-check', 'react-loss'), 1800);
}

function renderArena(): void {
  document.body.classList.toggle('arena-active', arenaActive);
  const opponent = PERSONALITIES[difficulty];
  const story = OPPONENT_STORIES[difficulty];
  query<HTMLImageElement>('#arenaPlayerImage').src = playerAvatar.image;
  query<HTMLElement>('#arenaPlayerName').textContent = playerAvatar.name;
  query<HTMLElement>('#arenaPlayerSide').textContent = `YOU・${playerSide === 'red' ? '紅方' : '黑方'}`;
  query<HTMLElement>('#arenaPlayerTitle').textContent = playerAvatar.title;
  query<HTMLImageElement>('#headerAvatarImage').src = playerAvatar.image;
  query<HTMLElement>('#headerAvatarName').textContent = playerAvatar.name;
  query<HTMLImageElement>('#arenaRivalImage').src = story.image;
  query<HTMLElement>('#arenaRivalChapter').textContent = `${story.chapter}・${story.location}・${playerSide === 'red' ? '黑方' : '紅方'}`;
  query<HTMLElement>('#arenaRivalName').textContent = opponent.name;
  query<HTMLElement>('#arenaRivalTitle').textContent = story.epithet;
  const compatibilityMode = preferredBackend(difficulty) === 'pikafish' && !supportsPikafishRuntime();
  query<HTMLElement>('#arenaElo').textContent = compatibilityMode
    ? `相容模式・棋力為近似值・${opponent.title}棋風`
    : `${eloLabel(difficulty)}・${opponent.title}棋風`;
}

function renderSelectionFlow(): void {
  const isSelection = !arenaActive;
  document.body.classList.toggle('selection-mode-active', isSelection && selectionStep === 'mode');
  document.body.classList.toggle('selection-opponents-active', isSelection && selectionStep === 'opponents');
  document.body.classList.toggle('selection-intro-active', isSelection && selectionStep === 'intro');
  const resumeButton = query<HTMLButtonElement>('#resumeGameBtn');
  const resumable = isResumableGame(state);
  resumeButton.disabled = !resumable;
  if (resumable) {
    const modeName = gameMode === 'challenge' ? '故事模式' : '自由模式';
    const sideName = playerSide === 'red' ? '持紅' : '持黑';
    query<HTMLElement>('#resumeGameDetail').textContent = `${PERSONALITIES[difficulty].name}・${modeName}・${sideName}・已走 ${state.moves.length} 手`;
    query<HTMLElement>('#resumeGameAction').textContent = '回到棋盤繼續 →';
    resumeButton.setAttribute('aria-label', `接續與${PERSONALITIES[difficulty].name}的棋局，${modeName}，${sideName}，已走 ${state.moves.length} 手`);
  } else {
    query<HTMLElement>('#resumeGameDetail').textContent = '目前沒有未完成的棋局。';
    query<HTMLElement>('#resumeGameAction').textContent = '尚無可接續棋局';
    resumeButton.setAttribute('aria-label', '目前沒有未完成的棋局');
  }
  const flow = query<HTMLElement>('#selectionFlow');
  flow.hidden = !isSelection || selectionStep === 'mode';
  if (flow.hidden) {
    lastAutoNarrationKey = null;
    return;
  }
  const isOpponentStep = selectionStep === 'opponents';
  query<HTMLElement>('#selectionStepLabel').textContent = isOpponentStep ? 'STEP 2 OF 3' : 'STEP 3 OF 3';
  query<HTMLElement>('#selectionFlowTitle').textContent = isOpponentStep ? '選擇對手' : '認識你的對手';
  query<HTMLElement>('#selectionFlowDescription').textContent = isOpponentStep
    ? '自由模式的十二位守局人都已開放，選一位想切磋的對手。'
    : gameMode === 'challenge'
      ? `故事模式目前來到${OPPONENT_STORIES[difficulty].chapter}；先讀完${PERSONALITIES[difficulty].name}的故事，再踏入棋局。`
      : `你選擇了${PERSONALITIES[difficulty].name}；先認識這位守局人，再決定是否進入棋局。`;
  query<HTMLButtonElement>('#selectionBackBtn').textContent = selectionStep === 'intro' && gameMode === 'practice'
    ? '← 返回對手選擇'
    : '← 返回模式選擇';
  const narrationKey = opponentIntroNarrationKey(arenaActive, selectionStep, difficulty);
  if (!narrationKey) {
    lastAutoNarrationKey = null;
  } else if (lastAutoNarrationKey !== narrationKey) {
    lastAutoNarrationKey = narrationKey;
    queueMicrotask(() => {
      narrateCurrentOpponent(true);
      prewarmPikafish();
    });
  }
}

function narrateCurrentOpponent(automatic = false): void {
  if (!gameAudio.getSettings().speech) {
    if (!automatic) showToast('角色語音目前已關閉，可在聲音設定中開啟');
    return;
  }
  const narratedDifficulty = difficulty;
  void gameAudio.unlock().then(() => {
    if (automatic && opponentIntroNarrationKey(arenaActive, selectionStep, difficulty) !== narratedDifficulty) return;
    gameAudio.narrate(narratedDifficulty, NARRATOR_INTROS[narratedDifficulty]);
  });
  showToast(`說書人${automatic ? '自動' : '正在'}介紹${PERSONALITIES[narratedDifficulty].name}`);
}

function enterArena(): void {
  arenaActive = true;
  window.clearTimeout(pikafishCheckoutTimer);
  gameAudio.stopSpeech();
  gameAudio.setMusicTheme('duel');
  renderArena();
  renderSelectionFlow();
  document.documentElement.scrollTop = 0;
  void gameAudio.unlock().then(() => speakOpponent('opening', OPPONENT_LINES[difficulty].opening, true));
  ensureOnlineMatchForCurrentGame();
  prewarmPikafish();
  if (state.outcome === 'ongoing' && state.sideToMove === oppositeSide(playerSide)) requestAiMove();
}

function leaveArena(): void {
  arenaActive = false;
  selectionStep = 'intro';
  gameAudio.stopSpeech();
  gameAudio.setMusicTheme('selection');
  render();
  schedulePikafishCheckout();
  document.documentElement.scrollTop = 0;
}

function previewAvatar(): void {
  const data = new FormData(query<HTMLFormElement>('#avatarDialog form'));
  const avatar = matchPlayerAvatar(data.get('avatarGender') as PlayerGender, data.get('avatarInterest') as PlayerInterest, data.get('avatarBuild') as 'light' | 'solid');
  query<HTMLImageElement>('#avatarPreview').src = avatar.image;
  query<HTMLElement>('#avatarPreviewName').textContent = avatar.name;
  query<HTMLElement>('#avatarPreviewTitle').textContent = avatar.title;
  query<HTMLButtonElement>('#useAvatarBtn').dataset.avatarId = avatar.id;
}

function openStory(showWorld = false): void {
  const opponent = PERSONALITIES[difficulty];
  const story = OPPONENT_STORIES[difficulty];
  query<HTMLImageElement>('#storyPortrait').src = showWorld ? WORLD_STORY.image : story.image;
  query<HTMLImageElement>('#storyPortrait').alt = showWorld ? '雲城十二位守局人' : `${opponent.name}肖像`;
  query<HTMLElement>('#storyChapter').textContent = showWorld ? 'THE TWELVE GAMES OF CLOUD GATE' : `${story.chapter}・${story.location}`;
  query<HTMLElement>('#storyName').textContent = showWorld ? WORLD_STORY.title : opponent.name;
  query<HTMLElement>('#storyEpithet').textContent = showWorld ? WORLD_STORY.subtitle : `${story.epithet}・${opponent.title}`;
  query<HTMLElement>('#storyBackstory').textContent = showWorld ? WORLD_STORY.intro : story.backstory;
  query<HTMLElement>('#storyEncounter').innerHTML = showWorld ? '<strong>你的棋路</strong><p>依序與十二位守局人交手。每一位都有不同棋風，也會在勝、負、和棋之後留下不同的話。</p>' : `<strong>相遇</strong><p>${story.encounter}</p>`;
  query<HTMLElement>('#storyLesson').innerHTML = showWorld ? '<strong>十二局之後</strong><p>前十一局各藏一種棋理；通過雲隱的終局，便能點亮一級檢定章。</p>' : `<strong>本局命題</strong><p>${story.lesson}</p>`;
  if (!storyDialog.open) storyDialog.showModal();
}

function renderMode(): void {
  document.querySelectorAll<HTMLButtonElement>('[data-game-mode]').forEach((button) => {
    const active = button.dataset.gameMode === gameMode;
    button.classList.toggle('active', active);
    button.setAttribute('aria-pressed', String(active));
  });
  const locked = !canChangeOpponent(gameMode, state);
  const fullClearScore = storyBestScore(difficulty, 'win');
  const currentClearScore = storyBestScoreAfterUndos(difficulty, 'win', undosUsed);
  query<HTMLElement>('.level-note').textContent = gameMode === 'practice'
    ? '自由模式：十二位角色全部開放，隨時選擇想切磋的棋風。'
    : '故事模式：沿著雲城棋路，擊敗前一位才會揭露下一位。';
  query<HTMLElement>('#modeRule').textContent = gameMode === 'practice'
    ? '自由切磋不計名望，十二位對手皆可選擇'
    : locked
      ? `對手已鎖定・已悔棋 ${undosUsed} 次・過關可得 ${currentClearScore}/${fullClearScore} 分`
      : `故事闖關・悔棋每次扣 20%・過關滿分 ${fullClearScore}`;
  query<HTMLElement>('#arenaModeName').textContent = gameMode === 'practice' ? '自由模式' : '故事模式';
  const undoButton = query<HTMLButtonElement>('#undoBtn');
  undoButton.innerHTML = gameMode === 'challenge' ? '<span>↶</span>悔棋 −20%' : '<span>↶</span>悔棋';
  undoButton.setAttribute('aria-label', gameMode === 'challenge' ? '悔棋，本關所得扣百分之二十' : '悔棋');
}

function renderBoard(): void {
  const legal = state.outcome === 'ongoing' && state.sideToMove === playerSide && !thinking ? generateLegalMoves(state.board, playerSide) : [];
  const destinations = selected === null ? new Set<number>() : new Set(legal.filter((move) => move.from === selected).map((move) => move.to));
  const lastMove = state.moves.at(-1);
  const fresh = Boolean(lastMove && activeMovePly === state.moves.length);
  const checkedSide = state.outcome === 'ongoing' && isInCheck(state.board, state.sideToMove) ? state.sideToMove : null;
  const boardIndices = Array.from({ length: state.board.length }, (_, index) => playerSide === 'red' ? index : state.board.length - 1 - index);
  boardCells.innerHTML = boardIndices.map((index) => {
    const piece = state.board[index];
    const classes = ['board-cell'];
    if (index === selected) classes.push('selected');
    if (destinations.has(index)) classes.push(piece ? 'capture-target' : 'move-target');
    if (lastMove?.from === index) classes.push('last-move', 'last-from', lastMove.piece.side === playerSide ? 'last-human' : 'last-ai');
    if (lastMove?.to === index) classes.push('last-move', 'last-to', lastMove.piece.side === playerSide ? 'last-human' : 'last-ai');
    if (fresh && lastMove?.from === index) classes.push('move-pulse-source', lastMove.piece.side === playerSide ? 'human-move' : 'ai-move');
    if (fresh && lastMove?.to === index) classes.push('move-pulse-target', lastMove.piece.side === playerSide ? 'human-move' : 'ai-move');
    if (piece?.type === 'K' && piece.side === checkedSide) classes.push('general-in-check');
    const label = piece ? `${piece.side === 'red' ? '紅方' : '黑方'}${pieceLabel(piece)}` : '空位';
    const draggable = piece?.side === playerSide && state.sideToMove === playerSide && state.outcome === 'ongoing' && !thinking;
    const pieceImages = piece
      ? `--piece-2d:url('${pieceImageUrl('2d', piece.side, piece.type)}');--piece-3d:url('${pieceImageUrl('3d', piece.side, piece.type)}')`
      : '';
    return `<button class="${classes.join(' ')}" data-index="${index}" role="gridcell" aria-label="第 ${Math.floor(index / 9) + 1} 列第 ${index % 9 + 1} 行，${label}">${piece ? `<span class="piece ${piece.side}" style="${pieceImages}" ${draggable ? 'data-draggable="true" aria-label="可拖曳"' : ''}></span>` : '<span class="hint"></span>'}</button>`;
  }).join('');
}

function renderCapturedPieces(): void {
  const { human, ai } = capturedPieces(state.moves, playerSide);
  const pieces = (items: typeof human) => items.length
    ? items.map((piece) => `<span class="captured-piece ${piece.side}" title="${piece.side === 'red' ? '紅方' : '黑方'}${pieceLabel(piece)}">${pieceLabel(piece)}</span>`).join('')
    : '<span class="no-captures">—</span>';
  query<HTMLElement>('#humanCaptures').innerHTML = pieces(human);
  query<HTMLElement>('#aiCaptures').innerHTML = pieces(ai);
}

function renderMatchInfo(): void {
  const { human, ai } = capturedPieces(state.moves, playerSide);
  const labels = (items: typeof human) => items.length
    ? items.map((piece) => `<span class="captured-piece ${piece.side}">${pieceLabel(piece)}</span>`).join('')
    : '<span class="no-captures">尚未吃子</span>';
  query<HTMLElement>('#matchCaptures').innerHTML = `<div class="match-capture-row"><strong>你吃掉</strong><div class="captured-pieces">${labels(human)}</div></div><div class="match-capture-row"><strong>對手吃掉</strong><div class="captured-pieces">${labels(ai)}</div></div>`;
  query<HTMLOListElement>('#matchHistory').innerHTML = state.moves.length
    ? state.moves.map((move, index) => `<li class="${index === state.moves.length - 1 ? 'latest' : ''}"><span>${index + 1}</span>${escapeHtml(move.notation)}</li>`).join('')
    : '<li class="empty-history">開局待下</li>';
}

function announceMove(move: MoveRecord, actor: 'human' | 'ai'): void {
  activeMovePly = state.moves.length;
  moveCue = { actor, move };
  const checked = state.outcome === 'ongoing' && isInCheck(state.board, state.sideToMove);
  void gameAudio.play(checked ? 'check' : move.captured ? 'capture' : 'move');
  if (actor === 'ai') {
    const lines = OPPONENT_LINES[difficulty];
    const message = checked ? lines.check : move.captured ? lines.capture : lines.reply;
    const cue: VoiceCue = checked ? 'check' : move.captured ? 'capture' : 'reply';
    setReaction('rival', message, checked ? 'check' : move.captured ? 'capture' : 'calm');
    if (state.outcome === 'ongoing') speakOpponent(cue, message, checked || Boolean(move.captured));
  } else if (checked) {
    const message = OPPONENT_LINES[difficulty].threatened;
    setReaction('rival', message, 'check');
    if (state.outcome === 'ongoing') speakOpponent('threatened', message, true);
  } else if (move.captured) {
    const message = OPPONENT_LINES[difficulty].hurt;
    setReaction('rival', message, 'loss');
    if (state.outcome === 'ongoing') speakOpponent('hurt', message, true);
  }
  window.clearTimeout(movePulseTimer);
  window.clearTimeout(moveCueTimer);
  if (actor === 'human') {
    movePulseTimer = window.setTimeout(() => {
      activeMovePly = null;
      renderBoard();
    }, 900);
  }
  moveCueTimer = window.setTimeout(() => {
    moveCue = null;
    render();
  }, actor === 'ai' ? 3600 : 1300);
}

function clearMoveFeedback(): void {
  window.clearTimeout(movePulseTimer);
  window.clearTimeout(moveCueTimer);
  activeMovePly = null;
  moveCue = null;
  lastSearchSummary = '';
  lastEngineUsed = null;
}

function renderBoardStyle(): void {
  boardFrame.dataset.boardStyle = boardStyle;
  document.querySelectorAll<HTMLButtonElement>('[data-board-style]').forEach((button) => {
    const active = button.dataset.boardStyle === boardStyle;
    button.classList.toggle('active', active);
    button.setAttribute('aria-pressed', String(active));
  });
}

function renderHistory(): void {
  const history = query<HTMLOListElement>('#moveHistory');
  query<HTMLElement>('#moveCount').textContent = `${state.moves.length} 手`;
  if (!state.moves.length) {
    history.innerHTML = '<li class="empty-history">開局待下</li>';
    return;
  }
  history.innerHTML = state.moves.slice(-24).map((move, index, visibleMoves) => `<li class="${index === visibleMoves.length - 1 ? 'latest' : ''}"><span>${state.moves.length - Math.min(24, state.moves.length) + index + 1}</span>${escapeHtml(move.notation)}</li>`).join('');
  history.scrollTop = history.scrollHeight;
}

function renderProfile(): void {
  const displayedScore = onlinePlayer?.overallScore ?? profile.score;
  query<HTMLElement>('#profileScore').textContent = displayedScore.toLocaleString('zh-TW');
  query<HTMLElement>('#rankTitle').textContent = onlineIdentity?.displayName ?? rankTitle(profile.score);
  query<HTMLElement>('#profileScoreLabel').textContent = onlinePlayer ? '總分' : '名望';
  query<HTMLElement>('#winsStat').textContent = String(profile.wins);
  query<HTMLElement>('#drawsStat').textContent = String(profile.draws);
  query<HTMLElement>('#lossesStat').textContent = String(profile.losses);
  query<HTMLElement>('#masterBadge').classList.toggle('earned', profile.masterWins > 0);
}

function renderChessBox(): void {
  const unlocked = DIFFICULTY_ORDER.filter((id) => profile.opponentRecords[id].wins > 0);
  query<HTMLElement>('#inscriptionGrid').innerHTML = DIFFICULTY_ORDER.map((id, index) => {
    const story = OPPONENT_STORIES[id];
    const isUnlocked = unlocked.includes(id);
    return `<div class="inscription-slot ${isUnlocked ? 'unlocked' : ''}"><span>${index + 1}</span><strong>${isUnlocked ? story.inscription : '？？'}</strong><small>${isUnlocked ? PERSONALITIES[id].name : '尚未解鎖'}</small></div>`;
  }).join('');
  const completed = query<HTMLElement>('#completedInscription');
  completed.classList.toggle('complete', unlocked.length === DIFFICULTY_ORDER.length);
  completed.textContent = unlocked.length === DIFFICULTY_ORDER.length
    ? WORLD_STORY.completedInscription
    : `已取得 ${unlocked.length}／${DIFFICULTY_ORDER.length} 段舊刻`;
}

function renderBoardEvent(checked: boolean): void {
  const event = query<HTMLElement>('#boardEvent');
  event.className = 'board-event';
  if (state.outcome === `${playerSide}_win`) {
    event.textContent = '勝';
    event.classList.add('show', 'win');
  } else if (state.outcome === `${oppositeSide(playerSide)}_win`) {
    event.textContent = '負';
    event.classList.add('show', 'loss');
  } else if (state.outcome === 'draw') {
    event.textContent = '和';
    event.classList.add('show', 'draw');
  } else if (checked) {
    event.textContent = '將軍';
    event.classList.add('show', 'check');
  }
}

function render(): void {
  renderOpponents();
  renderBoard();
  renderHistory();
  renderCapturedPieces();
  renderProfile();
  renderBoardStyle();
  renderMode();
  renderArena();
  renderSelectionFlow();
  renderAudioControls();
  query<HTMLElement>('#matchStatus').textContent = outcomeText();
  const checked = state.outcome === 'ongoing' && isInCheck(state.board, state.sideToMove);
  query<HTMLElement>('#checkStatus').textContent = checked ? '將軍！' : '';
  query<HTMLElement>('#matchDetail').textContent = thinking
    ? `${PERSONALITIES[difficulty].title}棋風・正在計算`
    : state.outcome !== 'ongoing'
      ? state.outcomeReason
      : checked
        ? (state.sideToMove === playerSide ? `你的${playerSide === 'red' ? '帥' : '將'}正被攻擊` : '對手正在應將')
        : state.sideToMove === playerSide && state.moves.length
          ? (moveCue?.actor === 'ai'
            ? `電腦：${moveCueText(moveCue.move)}${lastEngineUsed === 'book' ? `・${lastSearchSummary}` : ''}`
            : (lastSearchSummary || '電腦已落子，請回應'))
          : state.sideToMove === oppositeSide(playerSide)
            ? '已收到你的落子'
            : `${playerSide === 'red' ? '你持紅先行' : '對手持紅先行'}`;
  query<HTMLElement>('#playHint').classList.toggle('hidden', state.moves.length > 0 || state.outcome !== 'ongoing');
  renderBoardEvent(checked);
  query<HTMLElement>('#turnDot').className = `turn-dot ${thinking ? 'thinking' : state.outcome !== 'ongoing' ? 'ended' : state.sideToMove}`;
  query<HTMLButtonElement>('#undoBtn').disabled = undoStack.length === 0 || !canUndo(gameMode, undosUsed) || state.outcome !== 'ongoing';
  query<HTMLButtonElement>('#resignBtn').disabled = state.outcome !== 'ongoing';
}

function recordOutcome(): OutcomeReward | null {
  const result = recordChallengeOutcome(profile, state, difficulty, gameMode, playerSide, undosUsed);
  if (result.profile !== profile) {
    profile = result.profile;
    saveProfile(profile);
  }
  return result.reward;
}

function persistAndRender(): void {
  const reward = recordOutcome();
  saveCurrentState();
  render();
  if (reward && lastResultGameId !== state.gameId) {
    lastResultGameId = state.gameId;
    window.setTimeout(() => {
      showResult(reward);
      void syncOnlineResult(reward);
    }, 180);
  }
}

function showResult(reward: OutcomeReward): void {
  gameAudio.setMusicTheme('result');
  const labels = reward.result === 'win'
    ? { seal: '勝', title: '漂亮拿下這一局', eyebrow: 'VICTORY' }
    : reward.result === 'loss'
      ? { seal: '負', title: '這局由對手拿下', eyebrow: 'MATCH COMPLETE' }
      : { seal: '和', title: '本局握手言和', eyebrow: 'DRAW' };
  const captures = state.moves.filter((move) => move.captured).length;
  query<HTMLElement>('#resultSeal').textContent = labels.seal;
  query<HTMLElement>('#resultSeal').className = `result-seal ${reward.result}`;
  query<HTMLElement>('#resultEyebrow').textContent = labels.eyebrow;
  query<HTMLElement>('#resultTitle').textContent = labels.title;
  query<HTMLElement>('#resultReason').textContent = state.outcomeReason;
  const resultStory = OPPONENT_STORIES[difficulty];
  const resultPortrait = query<HTMLImageElement>('#resultPortrait');
  const resultCharacter = query<HTMLElement>('#resultCharacter');
  const portraitLabel = query<HTMLElement>('#resultPortraitLabel');
  resultCharacter.classList.toggle('opponent-defeated', reward.result === 'win');
  resultCharacter.classList.toggle('player-defeated', reward.result === 'loss');
  if (reward.result === 'win') {
    resultPortrait.src = resultStory.defeatImage;
    resultPortrait.alt = `${PERSONALITIES[difficulty].name}落敗後的肖像`;
    portraitLabel.textContent = '守關者落敗';
  } else if (reward.result === 'loss') {
    resultPortrait.src = playerAvatar.image;
    resultPortrait.alt = `${playerAvatar.name}落敗後的肖像`;
    portraitLabel.textContent = '旅人落敗';
  } else {
    resultPortrait.src = resultStory.image;
    resultPortrait.alt = `${PERSONALITIES[difficulty].name}肖像`;
    portraitLabel.textContent = '握手言和';
  }
  const resultQuote = resultStoryQuote(difficulty, reward.result);
  query<HTMLElement>('#resultStoryQuote').textContent = resultQuote;
  query<HTMLElement>('#resultFacts').innerHTML = `
    <span><small>對手</small><strong>${PERSONALITIES[difficulty].name}</strong></span>
    <span><small>模式</small><strong>${gameMode === 'challenge' ? '故事模式' : '自由模式'}</strong></span>
    <span><small>手數</small><strong>${state.moves.length}</strong></span>
    <span><small>吃子</small><strong>${captures}</strong></span>
  `;
  const lessonReview = analyzePostgame(difficulty, state, playerSide, reward.result);
  const lessonSection = query<HTMLElement>('#resultLessonReview');
  lessonSection.dataset.tone = lessonReview.tone;
  query<HTMLElement>('#resultLessonVerdict').textContent = lessonReview.verdict;
  query<HTMLElement>('#resultLessonTitle').textContent = lessonReview.title;
  query<HTMLElement>('#resultLessonSummary').textContent = lessonReview.summary;
  query<HTMLElement>('#resultLessonMetrics').innerHTML = lessonReview.metrics
    .map((metric) => `<span><small>${escapeHtml(metric.label)}</small><strong>${escapeHtml(metric.value)}</strong></span>`)
    .join('');
  query<HTMLElement>('#resultLessonHighlight').textContent = lessonReview.highlight;
  const pointText = reward.points > 0 ? `+${reward.points}` : String(reward.points);
  const firstInscription = reward.recorded && reward.result === 'win' && profile.opponentRecords[difficulty].wins === 1;
  const currentIndex = DIFFICULTY_ORDER.indexOf(difficulty);
  const newlyRevealed = firstInscription && currentIndex >= 0 && currentIndex < DIFFICULTY_ORDER.length - 1
    ? nextDifficulty(difficulty)
    : null;
  const undoText = reward.undoPenalty > 0 ? `悔棋扣 ${reward.undoPenalty} 分・` : '';
  query<HTMLElement>('#resultReward').textContent = reward.recorded
    ? `${firstInscription ? `匣底浮現舊刻「${resultStory.inscription}」・` : ''}${newlyRevealed ? `下一位守關者已現身・` : ''}${undoText}本局已正式記錄・名望 ${pointText}`
    : '這是自由模式，不計名望與正式戰績';
  query<HTMLElement>('#resultReward').classList.toggle('inscription-reveal', firstInscription);
  const onlineDelta = query<HTMLElement>('#onlineResultDelta');
  onlineDelta.textContent = onlineIdentity ? '正在同步站內排行榜…' : '填寫棋士名字後，之後的對局可登上排行榜';
  onlineDelta.classList.remove('show');
  const nextButton = query<HTMLButtonElement>('#nextOpponentBtn');
  nextButton.hidden = !newlyRevealed;
  nextButton.disabled = !newlyRevealed;
  nextButton.textContent = newlyRevealed ? `迎戰${PERSONALITIES[newlyRevealed].name}` : '挑戰下一位';
  void gameAudio.play(reward.result);
  speakOpponent(reward.result, resultQuote, true);
  if (!resultDialog.open) resultDialog.showModal();
}

function askConfirmation(title: string, message: string, actionLabel: string, action: () => void): void {
  query<HTMLElement>('#confirmTitle').textContent = title;
  query<HTMLElement>('#confirmMessage').textContent = message;
  query<HTMLButtonElement>('#confirmActionBtn').textContent = actionLabel;
  confirmAction = action;
  confirmDialog.showModal();
}

function cancelAi(terminate = false): void {
  window.clearTimeout(aiWatchdogTimer);
  window.clearTimeout(bookMoveTimer);
  aiWatchdogTimer = 0;
  bookMoveTimer = 0;
  jobId += 1;
  if (shouldSoftCancelPikafish(workerBackend, pikafishReady, terminate)) worker?.postMessage({ type: 'cancel' });
  else {
    if (workerBackend === 'pikafish') {
      pikafishReady = false;
      pikafishPrewarming = false;
    }
    worker?.terminate();
    worker = null;
    workerBackend = null;
  }
  thinking = false;
}

function requestAiMove(forceV1 = false): void {
  if (state.outcome !== 'ongoing' || state.sideToMove !== oppositeSide(playerSide)) return;
  const id = ++jobId;
  const bookMove = lookupOpeningMove(state, difficulty);
  if (bookMove) {
    thinking = true;
    lastEngineUsed = 'book';
    lastSearchSummary = `${bookMove.name}・熟譜`;
    render();
    const delay = 520 + ((state.seed + state.moves.length * 137 + bookMove.candidateCount * 53) % 480);
    bookMoveTimer = window.setTimeout(() => {
      bookMoveTimer = 0;
      if (jobId !== id || state.outcome !== 'ongoing' || state.sideToMove !== oppositeSide(playerSide)) return;
      thinking = false;
      undoStack.push(state);
      state = playMove(state, bookMove.move);
      const move = state.moves.at(-1);
      if (move) announceMove(move, 'ai');
      persistAndRender();
    }, delay);
    return;
  }
  const preferred = forceV1 ? 'v1' : preferredBackend(difficulty);
  const backend: EngineBackend = preferred === 'pikafish' && supportsPikafishRuntime() ? 'pikafish' : 'v1';
  const clearWatchdog = () => {
    window.clearTimeout(aiWatchdogTimer);
    aiWatchdogTimer = 0;
  };
  const armPikafishWatchdog = () => {
    if (backend !== 'pikafish') return;
    if (aiWatchdogTimer) return;
    aiWatchdogTimer = window.setTimeout(() => {
      if (jobId !== id || !thinking || workerBackend !== 'pikafish') return;
      lastSearchSummary = '進階棋力仍在準備，不會降低棋力';
      query<HTMLElement>('#matchDetail').textContent = lastSearchSummary;
      showToast('進階棋力仍在準備；本局不會自動降級');
    }, 8_000);
  };
  if (preferred === 'pikafish' && backend === 'v1' && !forceV1) {
    showToast('此瀏覽器未啟用進階引擎，已由 V1 接手');
  }
  if (worker && workerBackend !== backend) {
    if (workerBackend === 'pikafish') {
      pikafishReady = false;
      pikafishPrewarming = false;
    }
    worker.terminate();
    worker = null;
    workerBackend = null;
  }
  if (backend === 'v1' && worker) {
    worker.terminate();
    worker = null;
  }
  worker ??= createSearchWorker(backend);
  workerBackend = backend;
  thinking = true;
  if (backend === 'pikafish') window.clearTimeout(pikafishCheckoutTimer);
  if (backend === 'pikafish') {
    lastSearchSummary = pikafishReady ? '進階棋力已就緒，正在思考' : pikafishPrewarming ? '進階棋力預熱中' : '正在啟動進階棋力';
  }
  render();
  worker.onmessage = (event: MessageEvent<SearchWorkerMessage>) => {
    if (event.data.type === 'status') {
      armPikafishWatchdog();
      lastSearchSummary = event.data.message;
      query<HTMLElement>('#matchDetail').textContent = event.data.message;
      return;
    }
    if (event.data.type === 'ready') {
      pikafishReady = true;
      pikafishPrewarming = false;
      lastSearchSummary = '進階棋力已就緒，正在思考';
      query<HTMLElement>('#matchDetail').textContent = lastSearchSummary;
      return;
    }
    if (event.data.type === 'error') {
      if (event.data.id && event.data.id !== id) return;
      clearWatchdog();
      worker?.terminate();
      worker = null;
      workerBackend = null;
      if (backend === 'pikafish') {
        pikafishReady = false;
        pikafishPrewarming = false;
      }
      if (backend === 'pikafish' && !forceV1) {
        showToast('進階引擎載入失敗，已由 V1 接手');
        requestAiMove(true);
      } else {
        thinking = false;
        render();
        showToast('本機引擎中斷，請重新落子或另開新局');
      }
      return;
    }
    if (event.data.type !== 'result' || event.data.id !== id || state.sideToMove !== oppositeSide(playerSide)) return;
    clearWatchdog();
    if (!isUsableResult(state, event.data)) {
      worker?.terminate();
      worker = null;
      workerBackend = null;
      if (backend === 'pikafish') {
        pikafishReady = false;
        pikafishPrewarming = false;
      }
      showToast('進階引擎回傳異常，已由 V1 接手');
      requestAiMove(true);
      return;
    }
    thinking = false;
    if (backend === 'v1') {
      worker?.terminate();
      worker = null;
      workerBackend = null;
    }
    if (event.data.move) {
      undoStack.push(state);
      state = playMove(state, event.data.move);
      lastEngineUsed = forceV1 && preferredBackend(difficulty) === 'pikafish' ? 'v1-fallback' : event.data.engine;
      lastSearchSummary = `${engineLabel(lastEngineUsed)}・${Math.round(event.data.elapsedMs)} ms・深度 ${event.data.depth}・${event.data.nodes.toLocaleString()} 節點`;
      const move = state.moves.at(-1);
      if (move) announceMove(move, 'ai');
      persistAndRender();
    }
  };
  worker.onerror = () => {
    clearWatchdog();
    worker?.terminate();
    worker = null;
    workerBackend = null;
    if (backend === 'pikafish') {
      pikafishReady = false;
      pikafishPrewarming = false;
    }
    if (backend === 'pikafish' && !forceV1) {
      showToast('進階引擎中斷，已由 V1 接手');
      requestAiMove(true);
    } else {
      thinking = false;
      render();
      showToast('對手思考中斷，請重新落子或另開新局');
    }
  };
  armPikafishWatchdog();
  worker.postMessage({ type: 'search', id, state, difficulty });
}

function playHumanMove(move: Move): void {
  undoStack.push(state);
  state = playMove(state, move);
  lastSearchSummary = '';
  const record = state.moves.at(-1);
  if (record) announceMove(record, 'human');
  selected = null;
  persistAndRender();
  if (state.outcome === 'ongoing') requestAiMove();
}

function newGame(force = false, nextOpponent: Difficulty = difficulty, nextMode: GameMode = gameMode): void {
  if (!isOpponentAvailable(nextMode, profile, nextOpponent)) {
    const index = DIFFICULTY_ORDER.indexOf(nextOpponent);
    const previous = index > 0 ? PERSONALITIES[DIFFICULTY_ORDER[index - 1]!].name : null;
    showToast(previous ? `先在故事模式中擊敗${previous}` : '這位守關者尚未現身');
    return;
  }
  if (!force && state.moves.length > 0 && state.outcome === 'ongoing') {
    askConfirmation('另開新局？', '目前棋局尚未結束；另開新局不會計入勝負。', '確定開新局', () => newGame(true, nextOpponent, nextMode));
    return;
  }
  cancelAi();
  clearMoveFeedback();
  difficulty = nextOpponent;
  gameMode = nextMode;
  state = createInitialGame();
  playerSide = randomSide();
  selected = null;
  undoStack = [];
  undosUsed = 0;
  lastResultGameId = null;
  persistAndRender();
  ensureOnlineMatchForCurrentGame();
  gameAudio.setMusicTheme(arenaActive ? 'duel' : 'selection', true);
  showToast(`已向${PERSONALITIES[difficulty].name}提出新局`);
  if (arenaActive) speakOpponent('opening', OPPONENT_LINES[difficulty].opening, true);
  if (arenaActive && state.sideToMove === oppositeSide(playerSide)) requestAiMove();
}

function undoTurn(): void {
  if (!undoStack.length) return;
  cancelAi();
  clearMoveFeedback();
  let restored = undoStack.pop();
  while (restored?.sideToMove !== playerSide && undoStack.length) restored = undoStack.pop();
  if (restored) state = restored;
  selected = null;
  if (gameMode === 'challenge') undosUsed += 1;
  persistAndRender();
  if (gameMode === 'challenge') {
    const fullScore = storyBestScore(difficulty, 'win');
    const currentScore = storyBestScoreAfterUndos(difficulty, 'win', undosUsed);
    showToast(`已悔棋 ${undosUsed} 次・過關分 ${currentScore}/${fullScore}`);
  } else {
    showToast('已悔棋一回合');
  }
}

function renderSaveList(): void {
  const saves = listNamedSaves();
  saveList.innerHTML = saves.length ? saves.map((save) => {
    const date = new Date(save.data.savedAt).toLocaleString('zh-TW', { dateStyle: 'short', timeStyle: 'short' });
    const opponent = PERSONALITIES[save.data.difficulty];
    const styleLabel = save.data.schemaVersion !== 1 && save.data.boardStyle === '3d' ? '3D' : '2D';
    const modeLabel = (save.data.schemaVersion === 3 || save.data.schemaVersion === 4) && save.data.gameMode === 'challenge' ? '故事模式' : '自由模式';
    const sideLabel = save.data.schemaVersion === 4 && save.data.playerSide === 'black' ? '持黑' : '持紅';
    return `<article class="save-item"><div><strong>${escapeHtml(save.data.name)}</strong><span>${date}・${opponent.title}・${modeLabel}・${sideLabel}・${styleLabel}・${save.data.state.moves.length} 手</span></div><div><button data-load="${escapeHtml(save.id)}">續局</button><button class="delete" data-delete="${escapeHtml(save.id)}" aria-label="刪除存檔">×</button></div></article>`;
  }).join('') : '<div class="empty-saves">還沒有手動存檔</div>';
}

function loadStored(input: unknown): void {
  const loaded = deserializeGame(input);
  if (!isOpponentAvailable(loaded.gameMode, profile, loaded.difficulty)) {
    const index = DIFFICULTY_ORDER.indexOf(loaded.difficulty);
    const previous = index > 0 ? PERSONALITIES[DIFFICULTY_ORDER[index - 1]!].name : null;
    showToast(previous ? `先在故事模式中擊敗${previous}，才能續下這份棋局` : '這位守關者尚未現身');
    return;
  }
  cancelAi();
  clearMoveFeedback();
  state = loaded.state;
  difficulty = loaded.difficulty;
  boardStyle = loaded.boardStyle;
  gameMode = loaded.gameMode;
  undosUsed = loaded.undosUsed;
  playerSide = loaded.playerSide;
  selected = null;
  undoStack = [];
  lastResultGameId = null;
  const carriedTicket = input && typeof input === 'object'
    ? (input as { onlineMatchTicket?: unknown }).onlineMatchTicket
    : undefined;
  if (carriedTicket) importPortableOnlineMatchTicket(carriedTicket, state.gameId, onlineIdentity);
  persistAndRender();
  saveDialog.close();
  void ensureOnlineMatchForCurrentGame();
  const rankedContinuation = !onlineIdentity || state.moves.length === 0 || hasOnlineMatchTicket(state.gameId, onlineIdentity);
  showToast(rankedContinuation
    ? `已載入「${loaded.name}」`
    : `已載入「${loaded.name}」；此續局沒有原始憑證，只記錄本機成績`);
  if (state.outcome === 'ongoing' && state.sideToMove === oppositeSide(playerSide)) requestAiMove();
}

function legalHumanMove(from: number, to: number): Move | undefined {
  return generateLegalMoves(state.board, playerSide).find((candidate) => candidate.from === from && candidate.to === to);
}

function chooseSquare(index: number): void {
  const piece = state.board[index];
  if (selected !== null) {
    const move = legalHumanMove(selected, index);
    if (move) {
      playHumanMove(move);
      return;
    }
  }
  selected = piece?.side === playerSide ? index : null;
  renderBoard();
}

function dragTargetAt(clientX: number, clientY: number): number | null {
  const cell = document.elementFromPoint(clientX, clientY)?.closest<HTMLElement>('[data-index]');
  return cell ? Number(cell.dataset.index) : null;
}

function clearDrag(): void {
  const active = dragState;
  dragState = null;
  active?.ghost?.remove();
  document.body.classList.remove('dragging-piece');
  boardCells.querySelector('.drag-over')?.classList.remove('drag-over');
  if (active && boardCells.hasPointerCapture(active.pointerId)) boardCells.releasePointerCapture(active.pointerId);
}

boardCells.addEventListener('pointerdown', (event) => {
  if (event.button !== 0 || thinking || state.outcome !== 'ongoing' || state.sideToMove !== playerSide) return;
  const button = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-index]');
  const from = Number(button?.dataset.index);
  const piece = state.board[from];
  if (!button || !piece || piece.side !== playerSide) return;
  event.preventDefault();
  selected = from;
  renderBoard();
  dragState = { pointerId: event.pointerId, from, originX: event.clientX, originY: event.clientY, dragging: false, ghost: null, over: null };
  boardCells.setPointerCapture(event.pointerId);
});

boardCells.addEventListener('pointermove', (event) => {
  const active = dragState;
  if (!active || active.pointerId !== event.pointerId) return;
  const distance = Math.hypot(event.clientX - active.originX, event.clientY - active.originY);
  if (!active.dragging && distance < 6) return;
  event.preventDefault();
  if (!active.dragging) {
    const piece = state.board[active.from];
    if (!piece) return;
    active.dragging = true;
    active.ghost = document.createElement('span');
    active.ghost.className = `piece ${piece.side} drag-ghost`;
    active.ghost.textContent = pieceLabel(piece);
    document.body.append(active.ghost);
    document.body.classList.add('dragging-piece');
  }
  if (active.ghost) {
    active.ghost.style.left = `${event.clientX}px`;
    active.ghost.style.top = `${event.clientY}px`;
  }
  boardCells.querySelector('.drag-over')?.classList.remove('drag-over');
  active.over = dragTargetAt(event.clientX, event.clientY);
  if (active.over !== null && legalHumanMove(active.from, active.over)) {
    boardCells.querySelector<HTMLElement>(`[data-index="${active.over}"]`)?.classList.add('drag-over');
  }
});

boardCells.addEventListener('pointerup', (event) => {
  const active = dragState;
  if (!active || active.pointerId !== event.pointerId) return;
  if (!active.dragging) {
    clearDrag();
    return;
  }
  event.preventDefault();
  const target = dragTargetAt(event.clientX, event.clientY);
  const move = target === null ? undefined : legalHumanMove(active.from, target);
  suppressClickUntil = performance.now() + 500;
  clearDrag();
  if (move) playHumanMove(move);
  else {
    renderBoard();
    showToast('這裡不能落子，棋子已回到原位');
  }
});

boardCells.addEventListener('pointercancel', clearDrag);
boardCells.addEventListener('lostpointercapture', () => { if (dragState) clearDrag(); });

boardCells.addEventListener('click', (event) => {
  if (performance.now() < suppressClickUntil) return;
  const button = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-index]');
  if (!button || thinking || state.outcome !== 'ongoing' || state.sideToMove !== playerSide) return;
  chooseSquare(Number(button.dataset.index));
});

query<HTMLElement>('.board-style-switch').addEventListener('click', (event) => {
  const button = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-board-style]');
  if (!button) return;
  boardStyle = button.dataset.boardStyle as BoardStyle;
  saveCurrentState();
  renderBoardStyle();
  showToast(boardStyle === '3d' ? '已切換為立體棋盤' : '已切換為平面棋盤');
});

query<HTMLButtonElement>('#fullscreenBtn').addEventListener('click', async () => {
  try {
    if (document.fullscreenElement) await document.exitFullscreen();
    else await document.documentElement.requestFullscreen();
  } catch {
    showToast('瀏覽器未允許全螢幕，仍會保持完整棋盤版面');
  }
});
document.addEventListener('pointerdown', () => { void gameAudio.unlock(); }, { capture: true, once: true });
document.addEventListener('keydown', () => { void gameAudio.unlock(); }, { capture: true, once: true });
query<HTMLButtonElement>('#audioBtn').addEventListener('click', () => {
  void gameAudio.unlock();
  renderAudioControls();
  audioDialog.showModal();
});
query<HTMLInputElement>('#effectsToggle').addEventListener('change', (event) => {
  const enabled = (event.currentTarget as HTMLInputElement).checked;
  gameAudio.updateSettings({ effects: enabled });
  renderAudioControls();
  if (enabled) void gameAudio.play('move');
});
query<HTMLInputElement>('#speechToggle').addEventListener('change', (event) => {
  gameAudio.updateSettings({ speech: (event.currentTarget as HTMLInputElement).checked });
  renderAudioControls();
});
query<HTMLInputElement>('#musicToggle').addEventListener('change', (event) => {
  gameAudio.updateSettings({ music: (event.currentTarget as HTMLInputElement).checked });
  renderAudioControls();
});
query<HTMLInputElement>('#musicVolume').addEventListener('input', (event) => {
  gameAudio.updateSettings({ musicVolume: Number((event.currentTarget as HTMLInputElement).value) });
  renderAudioControls();
});
document.querySelectorAll<HTMLInputElement>('input[name="speechFrequency"]').forEach((input) => input.addEventListener('change', () => {
  if (!input.checked) return;
  gameAudio.updateSettings({ speechFrequency: input.value === 'all' ? 'all' : 'key' });
  renderAudioControls();
}));
query<HTMLButtonElement>('#previewVoiceBtn').addEventListener('click', () => {
  void gameAudio.unlock().then(() => speakOpponent('opening', OPPONENT_LINES[difficulty].opening, true));
});
document.addEventListener('fullscreenchange', () => {
  syncViewportHeight();
  query<HTMLButtonElement>('#fullscreenBtn').textContent = document.fullscreenElement ? '⛶ 離開全螢幕' : '⛶ 全螢幕';
});

function selectGameMode(event: Event): void {
  const button = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-game-mode]');
  if (!button) return;
  const nextMode = button.dataset.gameMode as GameMode;
  const nextOpponent = nextMode === 'challenge' ? highestRevealedDifficulty(profile) : difficulty;
  const nextStep: SelectionStep = nextMode === 'challenge' ? 'intro' : 'opponents';
  const applySelection = () => {
    arenaActive = false;
    selectionStep = nextStep;
    gameAudio.stopSpeech();
    gameAudio.setMusicTheme('selection');
    if (state.outcome !== 'ongoing' || nextOpponent !== difficulty) {
      newGame(true, nextOpponent, nextMode);
    } else {
      gameMode = nextMode;
      undosUsed = 0;
      saveCurrentState();
      render();
      showToast(nextMode === 'challenge'
        ? `故事模式：前往${PERSONALITIES[nextOpponent].name}的人物介紹`
        : '自由模式：請從十二位角色中選擇對手');
    }
    document.documentElement.scrollTop = 0;
  };
  if (nextMode !== gameMode && state.moves.length > 0 && state.outcome === 'ongoing') {
    askConfirmation(
      `改為${nextMode === 'challenge' ? '故事模式' : '自由模式'}？`,
      '模式只能在開局前更換；確認後會另開一局。',
      '換模式並開新局',
      applySelection,
    );
    return;
  }
  applySelection();
}

query<HTMLElement>('.journey-mode-grid').addEventListener('click', selectGameMode);
query<HTMLButtonElement>('#resumeGameBtn').addEventListener('click', () => {
  if (!isResumableGame(state)) {
    showToast('目前沒有未完成的棋局');
    return;
  }
  enterArena();
});

query<HTMLButtonElement>('#selectionBackBtn').addEventListener('click', () => {
  if (selectionStep === 'intro' && gameMode === 'practice') selectionStep = 'opponents';
  else selectionStep = 'mode';
  gameAudio.stopSpeech();
  render();
  document.documentElement.scrollTop = 0;
});

opponentList.addEventListener('click', (event) => {
  const button = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-difficulty]');
  if (!button) return;
  const next = button.dataset.difficulty as Difficulty;
  if (!isOpponentAvailable(gameMode, profile, next)) {
    const index = DIFFICULTY_ORDER.indexOf(next);
    const previous = index > 0 ? PERSONALITIES[DIFFICULTY_ORDER[index - 1]!].name : null;
    showToast(previous ? `先在故事模式中擊敗${previous}` : '這位守關者尚未現身');
    return;
  }
  if (next === difficulty && state.outcome === 'ongoing') {
    selectionStep = 'intro';
    render();
    document.documentElement.scrollTop = 0;
    return;
  }
  if (!canChangeOpponent(gameMode, state)) {
    showToast('故事模式落子後會鎖定對手，請完成本局或另開新局');
    return;
  }
  if (state.outcome !== 'ongoing') {
    resultDialog.close();
    selectionStep = 'intro';
    newGame(true, next, gameMode);
    document.documentElement.scrollTop = 0;
    return;
  }
  if (state.moves.length > 0) {
    askConfirmation(
      `改與${PERSONALITIES[next].name}對局？`,
      '每位角色都有獨立棋力與棋風；更換對手會結束目前棋局，並從初始盤面重新開始。',
      '換人並開新局',
      () => {
        selectionStep = 'intro';
        newGame(true, next, gameMode);
        document.documentElement.scrollTop = 0;
      },
    );
    return;
  }
  selectionStep = 'intro';
  newGame(true, next, gameMode);
  document.documentElement.scrollTop = 0;
});

query<HTMLButtonElement>('#newGameBtn').addEventListener('click', () => newGame());
query<HTMLButtonElement>('#undoBtn').addEventListener('click', undoTurn);
query<HTMLButtonElement>('#saveBtn').addEventListener('click', () => { renderSaveList(); saveDialog.showModal(); });
query<HTMLButtonElement>('#rulesBtn').addEventListener('click', () => rulesDialog.showModal());
query<HTMLButtonElement>('#matchInfoBtn').addEventListener('click', () => {
  renderMatchInfo();
  matchInfoDialog.showModal();
});
query<HTMLButtonElement>('#matchFullRulesBtn').addEventListener('click', () => {
  matchInfoDialog.close();
  rulesDialog.showModal();
});
document.querySelectorAll<HTMLButtonElement>('[data-match-tab]').forEach((button) => button.addEventListener('click', () => {
  const tab = button.dataset.matchTab;
  document.querySelectorAll<HTMLButtonElement>('[data-match-tab]').forEach((item) => item.setAttribute('aria-selected', String(item === button)));
  document.querySelectorAll<HTMLElement>('[data-match-panel]').forEach((panel) => panel.classList.toggle('active', panel.dataset.matchPanel === tab));
}));
query<HTMLButtonElement>('#worldStoryBtn').addEventListener('click', () => openStory(true));
query<HTMLButtonElement>('#chessBoxBtn').addEventListener('click', () => {
  renderChessBox();
  chessBoxDialog.showModal();
});
query<HTMLButtonElement>('#leaderboardBtn').addEventListener('click', openLeaderboard);
query<HTMLButtonElement>('#homeLeaderboardBtn').addEventListener('click', openLeaderboard);
document.querySelectorAll<HTMLButtonElement>('[data-close-leaderboard]').forEach((button) => button.addEventListener('click', () => leaderboardDialog.close()));
document.querySelectorAll<HTMLButtonElement>('[data-leaderboard-tab]').forEach((button) => button.addEventListener('click', () => {
  leaderboardTab = button.dataset.leaderboardTab as keyof LeaderboardSnapshot['leaderboards'];
  renderLeaderboard();
  if (leaderboardSnapshot) query<HTMLElement>('#leaderboardStatus').textContent = `顯示前 ${leaderboardSnapshot.leaderboards[leaderboardTab].length} 名・剛剛更新`;
}));
query<HTMLButtonElement>('#accountSettingsBtn').addEventListener('click', () => {
  leaderboardDialog.close();
  openPlayerNameDialog(onlinePlayer?.hasPassword === false ? 'upgrade' : 'edit');
});
document.querySelectorAll<HTMLButtonElement>('[data-account-mode]').forEach((button) => button.addEventListener('click', () => {
  openPlayerNameDialog(button.dataset.accountMode as 'register' | 'login');
}));
query<HTMLButtonElement>('#offlinePlayBtn').addEventListener('click', () => {
  playerNameDialog.close();
  showToast(accountDialogMode === 'edit'
    ? '已取消更改名字'
    : accountDialogMode === 'upgrade'
      ? '稍後仍可從排行榜設定跨裝置密碼'
      : '目前以離線棋士身分遊玩，成績不會送上排行榜');
});
query<HTMLFormElement>('#playerNameForm').addEventListener('submit', async (event) => {
  event.preventDefault();
  const input = query<HTMLInputElement>('#playerNameInput');
  const passwordInput = query<HTMLInputElement>('#playerPasswordInput');
  const passwordConfirm = query<HTMLInputElement>('#playerPasswordConfirmInput');
  const button = query<HTMLButtonElement>('#savePlayerNameBtn');
  const error = query<HTMLElement>('#playerNameError');
  const name = input.value.trim().replace(/\s+/g, ' ');
  if (name.length < 2 || name.length > 16) {
    error.textContent = '請輸入 2–16 個字的棋士名字';
    return;
  }
  const needsPassword = accountDialogMode !== 'edit';
  if (needsPassword && passwordInput.value.length < 4) {
    error.textContent = '密碼至少需要 4 個字元，不限格式';
    return;
  }
  if ((accountDialogMode === 'register' || accountDialogMode === 'upgrade') && passwordInput.value !== passwordConfirm.value) {
    error.textContent = '兩次輸入的密碼不同';
    return;
  }
  const idleLabel = button.textContent || '送出';
  button.disabled = true;
  button.textContent = accountDialogMode === 'login' ? '正在登入…' : '正在安全建立帳號…';
  error.textContent = '';
  try {
    if (accountDialogMode === 'register') {
      const result = await registerOnlinePlayer(name, passwordInput.value);
      onlineIdentity = result.identity;
      onlinePlayer = result.player;
    } else if (accountDialogMode === 'login') {
      const result = await loginOnlinePlayer(name, passwordInput.value);
      onlineIdentity = result.identity;
      onlinePlayer = result.player;
    } else if (accountDialogMode === 'edit') {
      const result = await renameOnlinePlayer(name);
      onlineIdentity = result.identity;
      onlinePlayer = result.player;
    } else {
      onlinePlayer = await setOnlinePassword(passwordInput.value);
    }
    if (!onlinePlayer) throw new Error('無法讀取棋士帳號');
    applyOnlineProgress(onlinePlayer);
    leaderboardSnapshot = null;
    playerNameDialog.close();
    renderProfile();
    showToast(accountDialogMode === 'login'
      ? `歡迎回來，${onlinePlayer.displayName}；這台裝置已接續你的進度`
      : accountDialogMode === 'upgrade'
        ? '跨裝置登入已啟用，原本的成績完整保留'
        : accountDialogMode === 'edit'
          ? `棋士名字已改為${onlinePlayer.displayName}`
          : `歡迎${onlinePlayer.displayName}，你的棋士成績會開始累積`);
    void refreshLeaderboard(false);
    ensureOnlineMatchForCurrentGame();
    if (accountDialogMode === 'register' || accountDialogMode === 'login' || accountDialogMode === 'upgrade') {
      lastCloudSignature = '';
      void restoreCloudGame();
    }
  } catch (reason) {
    error.textContent = reason instanceof Error ? reason.message : '無法完成棋士帳號操作，請稍後再試';
  } finally {
    button.disabled = false;
    button.textContent = idleLabel;
  }
});
query<HTMLElement>('#opponentBrief').addEventListener('click', (event) => {
  const target = event.target as HTMLElement;
  if (target.closest('[data-narrate-opponent]')) {
    narrateCurrentOpponent();
  }
  if (target.closest('[data-start-match]')) enterArena();
});
query<HTMLButtonElement>('#leaveArenaBtn').addEventListener('click', leaveArena);
document.querySelectorAll<HTMLButtonElement>('[data-change-avatar]').forEach((button) => button.addEventListener('click', () => {
  previewAvatar();
  avatarDialog.showModal();
}));
avatarDialog.addEventListener('change', previewAvatar);
query<HTMLButtonElement>('#useAvatarBtn').addEventListener('click', (event) => {
  const avatar = PLAYER_AVATARS.find((item) => item.id === (event.currentTarget as HTMLButtonElement).dataset.avatarId);
  if (!avatar) return;
  playerAvatar = avatar;
  localStorage.setItem('cloud-xiangqi-player-avatar', avatar.id);
  avatarDialog.close();
  renderArena();
  showToast(`你的形象已改為${avatar.name}`);
});
storyDialog.addEventListener('click', (event) => {
  if ((event.target as HTMLElement).closest('[data-close-story]')) storyDialog.close();
});
query<HTMLButtonElement>('#resignBtn').addEventListener('click', () => {
  if (state.outcome !== 'ongoing') return;
  askConfirmation('確定認輸？', '本局會立即結束；故事模式將正式記錄敗局。', '確認認輸', () => {
    cancelAi();
    state = declareOutcome(state, `${oppositeSide(playerSide)}_win`, '你已認輸');
    persistAndRender();
  });
});

query<HTMLButtonElement>('#confirmActionBtn').addEventListener('click', () => {
  const action = confirmAction;
  confirmAction = null;
  confirmDialog.close();
  action?.();
});
confirmDialog.addEventListener('close', () => { confirmAction = null; });

query<HTMLButtonElement>('#rematchBtn').addEventListener('click', () => {
  resultDialog.close();
  gameAudio.setMusicTheme('duel');
  newGame(true, difficulty, gameMode);
});
query<HTMLButtonElement>('#nextOpponentBtn').addEventListener('click', () => {
  const next = nextDifficulty(difficulty);
  if (!isOpponentRevealed(profile, next) || DIFFICULTY_ORDER.indexOf(next) <= DIFFICULTY_ORDER.indexOf(difficulty)) return;
  resultDialog.close();
  arenaActive = false;
  selectionStep = 'intro';
  gameAudio.setMusicTheme('selection');
  newGame(true, next, gameMode);
  document.documentElement.scrollTop = 0;
});

query<HTMLButtonElement>('#createSaveBtn').addEventListener('click', () => {
  putNamedSave(state, difficulty, boardStyle, saveName.value || `${PERSONALITIES[difficulty].title}・第 ${state.moves.length} 手`, gameMode, undosUsed, playerSide);
  saveName.value = '';
  renderSaveList();
  showToast('棋局已存入這台裝置');
});

saveList.addEventListener('click', (event) => {
  const target = (event.target as HTMLElement).closest<HTMLButtonElement>('button');
  if (!target) return;
  const loadId = target.dataset.load;
  const deleteId = target.dataset.delete;
  if (loadId) {
    const save = listNamedSaves().find((item) => item.id === loadId);
    if (save) loadStored(save.data);
  }
  if (deleteId) {
    deleteNamedSave(deleteId);
    renderSaveList();
  }
});

query<HTMLButtonElement>('#exportBtn').addEventListener('click', () => {
  const data = serializeGame(state, difficulty, boardStyle, saveName.value || `雲端將族-${PERSONALITIES[difficulty].title}`, gameMode, undosUsed, playerSide);
  const ticket = portableOnlineMatchTicket(state.gameId, onlineIdentity);
  const portableData = ticket ? { ...data, onlineMatchTicket: ticket } : data;
  const blob = new Blob([JSON.stringify(portableData, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `cloud-xiangqi-${new Date().toISOString().slice(0, 10)}.json`;
  link.click();
  URL.revokeObjectURL(url);
  showToast(ticket ? '已匯出棋局 JSON，包含本局的跨裝置計分憑證' : '已匯出棋局 JSON');
});

query<HTMLButtonElement>('#importBtn').addEventListener('click', () => importInput.click());
importInput.addEventListener('change', async () => {
  const file = importInput.files?.[0];
  if (!file) return;
  try {
    loadStored(JSON.parse(await file.text()));
  } catch (error) {
    showToast(error instanceof Error ? error.message : '無法讀取這份存檔');
  } finally {
    importInput.value = '';
  }
});

window.addEventListener('pagehide', () => {
  const identity = onlineIdentity;
  if (!identity || !isResumableGame(state)) return;
  const snapshot = serializeGame(state, difficulty, boardStyle, '雲端續局', gameMode, undosUsed, playerSide);
  void saveCloudGame(snapshot, identity, true).catch(() => {
    // The local autosave remains available when the browser cannot finish a keepalive request.
  });
});

render();
gameAudio.setMusicTheme('selection');
scheduleHomePikafishPrewarm();
if (!restored) saveAutosave(state, difficulty, boardStyle, gameMode, undosUsed, playerSide);
if (restored && isResumableGame(state)) showToast('找到一份未完棋局，可從「接續上次棋局」繼續');
if (onlineIdentity) {
  void refreshLeaderboard(false).then(() => {
    ensureOnlineMatchForCurrentGame();
    void restoreCloudGame();
  });
}
else window.setTimeout(openPlayerNameDialog, 450);
